import { useState, useEffect, useRef, Fragment } from 'react';
import logo from './logo.png';
import toast, { Toaster } from 'react-hot-toast';
import GanttChart from './GanttChart';
import { SignedIn, SignedOut, SignInButton, UserButton, useUser } from '@clerk/clerk-react';

type Task = {
  id: number;
  phase_name: string;
  name: string;
  description: string;
  assignee_id: number | null;
  assignee_name: string | null;
  start_date: string | null;
  end_date: string | null;
  estimated_days: number;
  status: string;
  priority: string;
  rag_status: string | null;
  rag_manual: boolean;
  progress_notes: string | null;
  percent_complete: number | null;
  task_order: number;
  depends_on_ids: number[];
};

type StatusReport = {
  id: number;
  project_id: number;
  created_at: string;
  summary: string;
  full_report: string;
  rag_status: 'Red' | 'Yellow' | 'Green' | null;
};

type TeamMember = {
  id: number;
  name: string | null;
  email: string | null;
  role: string;
  allocated_hours: number | null;
};

type LaborItem = {
  id: number;
  project_id: number;
  role_id: number | null;
  role_name: string | null;
  hourly_rate: number | null;
  hours: number;
  team_created: boolean;
};

type NonLaborItem = {
  id: number;
  project_id: number;
  description: string;
  category: string | null;
  units: number;
  unit_cost: number;
};

type BudgetNonLabor = {
  id: number;
  project_id: number;
  description: string;
  units: number;
  rate: number;
};

type TeamActual = {
  id: number;
  team_member_id: number;
  week_start: string;
  hours: number;
};

type Role = {
  id: number;
  user_id: string;
  name: string;
  hourly_rate: number | null;
};

type Resource = {
  id: number;
  user_id: string;
  name: string;
  email: string | null;
  role: string | null;
  hourly_rate: number | null;
};

type Project = {
  id: number;
  name: string;
  description: string;
  plan: any;
  created_at: string;
  project_rag: 'Red' | 'Yellow' | 'Green' | null;
  project_state: 'Not Started' | 'In Progress' | 'Complete' | null;
};

// Count working (or calendar) days from dateA to dateB
function workingDayDelta(fromStr: string, toStr: string, includeWeekends: boolean): number {
  const from = new Date(fromStr + 'T12:00:00Z');
  const to = new Date(toStr + 'T12:00:00Z');
  if (includeWeekends) return Math.round((to.getTime() - from.getTime()) / 86400000);
  const dir = to >= from ? 1 : -1;
  let count = 0;
  const cursor = new Date(from);
  while (cursor.toISOString().split('T')[0] !== toStr) {
    cursor.setUTCDate(cursor.getUTCDate() + dir);
    const day = cursor.getUTCDay();
    if (day !== 0 && day !== 6) count += dir;
  }
  return count;
}

// Advance a date string by N working (or calendar) days
function advanceDateJS(dateStr: string, days: number, includeWeekends: boolean): string {
  if (!dateStr || days === 0) return dateStr;
  const d = new Date(dateStr + 'T12:00:00Z');
  if (includeWeekends) {
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().split('T')[0];
  }
  const dir = days > 0 ? 1 : -1;
  let remaining = Math.abs(days);
  while (remaining > 0) {
    d.setUTCDate(d.getUTCDate() + dir);
    if (d.getUTCDay() !== 0 && d.getUTCDay() !== 6) remaining--;
  }
  return d.toISOString().split('T')[0];
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const STRIPE_PRO_PRICE_ID = process.env.REACT_APP_STRIPE_PRO_PRICE_ID || 'price_1TotCyQWTU0Vi0ahk6tJnEY0';
const STRIPE_UNLIMITED_PRICE_ID = process.env.REACT_APP_STRIPE_UNLIMITED_PRICE_ID || 'price_1TotCwQWTU0Vi0ah2lbp1WsZ';

const apiFetch = async (url: string, options?: RequestInit): Promise<any> => {
  const res = await fetch(url, options);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err: any = new Error(body.error || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
};

// Returns the server's error message when available, or the action-specific
// fallback when the error is a generic network failure ("Failed to fetch").
const errMsg = (err: any, fallback: string): string =>
  (err?.message && err.message !== 'Failed to fetch') ? err.message : fallback;

function App() {
  const [description, setDescription] = useState('');
  const [plan, setPlan] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [view, setView] = useState<'dashboard' | 'new' | 'plan' | 'tasks' | 'team' | 'gantt' | 'reports' | 'costs' | 'budget' | 'billing-success' | 'welcome'>('dashboard');
  const [laborExpanded, setLaborExpanded] = useState(false);
  const [nonLaborExpanded, setNonLaborExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [newMember, setNewMember] = useState({ name: '', email: '', role: '', allocated_hours: '' });
  const [addingMember, setAddingMember] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState<number | null>(null);
  const [memberHoursDraft, setMemberHoursDraft] = useState('');
  const [actuals, setActuals] = useState<TeamActual[]>([]);
  const [editingActualKey, setEditingActualKey] = useState<string | null>(null);
  const [actualsDraft, setActualsDraft] = useState('');
  const [hoveredMemberId, setHoveredMemberId] = useState<number | null>(null);
  const [budgetNonLabor, setBudgetNonLabor] = useState<BudgetNonLabor[]>([]);
  const [newBudgetNL, setNewBudgetNL] = useState({ description: '', units: '', rate: '' });
  const [editingBudgetNLId, setEditingBudgetNLId] = useState<number | null>(null);
  const [budgetNLDraft, setBudgetNLDraft] = useState<{ description: string; units: string; rate: string }>({ description: '', units: '', rate: '' });
  const [planStartDate, setPlanStartDate] = useState('');
  const [inlineEdit, setInlineEdit] = useState<{ taskId: number; field: string; initialValue?: string } | null>(null);
  const dragTaskId = useRef<number | null>(null);
  const [dragOverTaskId, setDragOverTaskId] = useState<number | null>(null);
  const [dragOverPhaseEnd, setDragOverPhaseEnd] = useState<string | null>(null);
  const [newTaskId, setNewTaskId] = useState<number | null>(null);
  const [settings, setSettings] = useState<{ include_weekends: boolean; auto_rag: boolean }>({ include_weekends: true, auto_rag: false });
  const [showSettings, setShowSettings] = useState(false);
const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [promptModal, setPromptModal] = useState<{ label: string; value: string; onSubmit: (val: string) => void } | null>(null);
  const [depsModalTaskId, setDepsModalTaskId] = useState<number | null>(null);
  const [depsModalSelected, setDepsModalSelected] = useState<number[]>([]);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showGanttExportMenu, setShowGanttExportMenu] = useState(false);
  const [showBudgetExportMenu, setShowBudgetExportMenu] = useState(false);
  const [planOrigin, setPlanOrigin] = useState<'new' | 'dashboard'>('new');
  const [reports, setReports] = useState<StatusReport[]>([]);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [editingProjectName, setEditingProjectName] = useState(false);
  const [projectNameDraft, setProjectNameDraft] = useState('');
  const [editingPhase, setEditingPhase] = useState<string | null>(null);
  const [phaseDraft, setPhaseDraft] = useState('');
  const [laborItems, setLaborItems] = useState<LaborItem[]>([]);
  const [nonLaborItems, setNonLaborItems] = useState<NonLaborItem[]>([]);
  const [newLaborRoleId, setNewLaborRoleId] = useState<string>('');
  const [newLaborHours, setNewLaborHours] = useState('');
  const [newNonLabor, setNewNonLabor] = useState({ description: '', category: '', units: '1', unit_cost: '' });
  const [editingLaborId, setEditingLaborId] = useState<number | null>(null);
  const [editingNonLaborId, setEditingNonLaborId] = useState<number | null>(null);
  const [laborDraft, setLaborDraft] = useState<{ roleId: string; hours: string }>({ roleId: '', hours: '' });
  const [nonLaborDraft, setNonLaborDraft] = useState<Partial<NonLaborItem>>({});
  const [roles, setRoles] = useState<Role[]>([]);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState<number | null>(null);
  const [roleDraft, setRoleDraft] = useState<Partial<Role>>({});
  const [newRole, setNewRole] = useState({ name: '', hourly_rate: '' });
  const [resources, setResources] = useState<Resource[]>([]);
  const [showResourcesModal, setShowResourcesModal] = useState(false);
  const [editingResourceId, setEditingResourceId] = useState<number | null>(null);
  const [resourceDraft, setResourceDraft] = useState<Partial<Resource>>({});
  const [newResource, setNewResource] = useState({ name: '', email: '', role: '', hourly_rate: '' });
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpMessages, setHelpMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([]);
  const [helpInput, setHelpInput] = useState('');
  const [helpLoading, setHelpLoading] = useState(false);
  const [supportEmailCopied, setSupportEmailCopied] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackEmailCopied, setFeedbackEmailCopied] = useState(false);
  const helpEndRef = useRef<HTMLDivElement>(null);
  const [cascadeModal, setCascadeModal] = useState<{
    cascadeChanges: { taskId: number; name: string; newStart: string; newEnd: string; estimatedDays: number }[];
    onSaveOnly: () => Promise<void>;
    onSaveWithCascade: () => Promise<void>;
  } | null>(null);
  const [editingReport, setEditingReport] = useState<StatusReport | null>(null);
  const ganttRef = useRef<HTMLDivElement>(null);
  const ganttScrollRef = useRef<HTMLDivElement>(null);
  const teamScrollRef = useRef<HTMLDivElement>(null);
  const teamHeaderScrollRef = useRef<HTMLDivElement>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [userTier, setUserTier] = useState<string>('trial');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const hasCheckedFirstLogin = useRef(false);
  const { user } = useUser();

  useEffect(() => {
    if (window.location.pathname === '/billing-success') setView('billing-success');
  }, []);

  useEffect(() => {
    if (user) {
      fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: user.id,
          email: user.primaryEmailAddress?.emailAddress,
          firstName: user.firstName,
          lastName: user.lastName
        })
      }).catch(err => console.error('User init failed:', err));
      loadProjects();
      loadSettings();
      loadResources();
      loadRoles();
      apiFetch(`${API_URL}/api/billing/status/${user.id}`)
        .then((data: any) => setUserTier(data.tier || 'trial'))
        .catch(() => {});
    }
  }, [user]);

  // Send first-time users (no existing projects) to /welcome instead of the dashboard.
  useEffect(() => {
    if (!user || !projectsLoaded || hasCheckedFirstLogin.current) return;
    if (window.location.pathname === '/billing-success') return;
    hasCheckedFirstLogin.current = true;

    const welcomeKey = `gaimplans_welcome_shown_${user.id}`;
    const alreadyShownOrOnboarded = localStorage.getItem(welcomeKey) === 'true' || projects.length > 0;

    if (!alreadyShownOrOnboarded) {
      localStorage.setItem(welcomeKey, 'true');
      window.history.replaceState(null, '', '/welcome');
      setView('welcome');
    } else if (window.location.pathname === '/welcome') {
      window.history.replaceState(null, '', '/');
      setView('dashboard');
    }
  }, [user, projectsLoaded, projects]);

  useEffect(() => {
    if (view === 'dashboard' && user) loadProjects();
  }, [view]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  useEffect(() => {
    helpEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [helpMessages]);

  useEffect(() => {
    if (!newTaskId) return;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-task-id="${newTaskId}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setInlineEdit({ taskId: newTaskId, field: 'name' });
      setNewTaskId(null);
    }, 50);
    return () => clearTimeout(timer);
  }, [newTaskId]);

  useEffect(() => {
    if (view !== 'team') return;
    const timer = setTimeout(() => {
      const bodyDiv = teamScrollRef.current;
      const headerDiv = teamHeaderScrollRef.current;
      if (!bodyDiv) return;
      const ths = Array.from((headerDiv ?? bodyDiv).querySelectorAll('th'));
      const idx = ths.findIndex(th => th.getAttribute('data-current-week') === 'true');
      if (idx >= 0) {
        const pos = Math.max(0, idx * 68 - 68);
        bodyDiv.scrollLeft = pos;
        if (headerDiv) headerDiv.scrollLeft = pos;
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [view]);

  const startCheckout = async (priceId: string) => {
    if (!user) return;
    setCheckoutLoading(true);
    try {
      const data = await apiFetch(`${API_URL}/api/billing/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, priceId })
      });
      window.location.href = data.url;
    } catch (err: any) {
      toast.error(errMsg(err, 'Failed to start checkout'));
      setCheckoutLoading(false);
    }
  };

  const openPortal = async () => {
    if (!user) return;
    try {
      const data = await apiFetch(`${API_URL}/api/billing/create-portal-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id })
      });
      window.location.href = data.url;
    } catch (err: any) {
      toast.error(errMsg(err, 'Failed to open billing portal'));
    }
  };

  const loadSettings = async () => {
    if (!user) return;
    try {
      const data = await apiFetch(`${API_URL}/api/settings/${user.id}`);
      setSettings(data);
    } catch (err: any) {
      toast.error(errMsg(err, 'Failed to load settings'));
    }
  };

  const saveSettings = async (updated: { include_weekends: boolean; auto_rag: boolean }) => {
    if (!user) return;
    setSettings(updated);
    try {
      await apiFetch(`${API_URL}/api/settings/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated)
      });
    } catch (err: any) { toast.error(errMsg(err, 'Failed to save settings')); }
  };

  const loadCosts = async (projectId: number) => {
    try {
      const data = await apiFetch(`${API_URL}/api/projects/${projectId}/costs`);
      setLaborItems(data.labor || []);
      setNonLaborItems(data.nonlabor || []);
    } catch (err: any) {
      toast.error(errMsg(err, 'Failed to load costs'));
    }
  };

  const addLaborItem = async () => {
    if (!currentProject || !newLaborRoleId) return;
    try {
      const item = await apiFetch(`${API_URL}/api/projects/${currentProject.id}/costs/labor`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roleId: newLaborRoleId, hours: newLaborHours || 0 })
      });
      setLaborItems(prev => [...prev, item]);
      setNewLaborRoleId(''); setNewLaborHours('');
    } catch (err: any) { toast.error(errMsg(err, 'Failed to add labor item')); }
  };

  const saveLaborEdit = async () => {
    if (!editingLaborId) return;
    try {
      const updated = await apiFetch(`${API_URL}/api/costs/labor/${editingLaborId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roleId: laborDraft.roleId || null, hours: laborDraft.hours || 0 })
      });
      setLaborItems(prev => prev.map(i => i.id === editingLaborId ? updated : i));
      setEditingLaborId(null); setLaborDraft({ roleId: '', hours: '' });
    } catch (err: any) { toast.error(errMsg(err, 'Failed to save labor item')); }
  };

  const deleteLaborItem = async (id: number) => {
    confirm('Delete this labor item?', async () => {
      try {
        await apiFetch(`${API_URL}/api/costs/labor/${id}`, { method: 'DELETE' });
        setLaborItems(prev => prev.filter(i => i.id !== id));
        toast.success('Labor item deleted');
      } catch (err: any) { toast.error(errMsg(err, 'Failed to delete labor item')); }
    });
  };

  const addNonLaborItem = async () => {
    if (!currentProject || !newNonLabor.description.trim()) return;
    try {
      const item = await apiFetch(`${API_URL}/api/projects/${currentProject.id}/costs/nonlabor`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: newNonLabor.description, category: newNonLabor.category, units: newNonLabor.units || 1, unitCost: newNonLabor.unit_cost || 0 })
      });
      setNonLaborItems(prev => [...prev, item]);
      setNewNonLabor({ description: '', category: '', units: '1', unit_cost: '' });
    } catch (err: any) { toast.error(errMsg(err, 'Failed to add non-labor item')); }
  };

  const saveNonLaborEdit = async () => {
    if (!editingNonLaborId) return;
    try {
      const updated = await apiFetch(`${API_URL}/api/costs/nonlabor/${editingNonLaborId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: nonLaborDraft.description, category: nonLaborDraft.category, units: nonLaborDraft.units, unitCost: nonLaborDraft.unit_cost })
      });
      setNonLaborItems(prev => prev.map(i => i.id === editingNonLaborId ? updated : i));
      setEditingNonLaborId(null); setNonLaborDraft({});
    } catch (err: any) { toast.error(errMsg(err, 'Failed to save non-labor item')); }
  };

  const deleteNonLaborItem = async (id: number) => {
    confirm('Delete this non-labor item?', async () => {
      try {
        await apiFetch(`${API_URL}/api/costs/nonlabor/${id}`, { method: 'DELETE' });
        setNonLaborItems(prev => prev.filter(i => i.id !== id));
        toast.success('Non-labor item deleted');
      } catch (err: any) { toast.error(errMsg(err, 'Failed to delete non-labor item')); }
    });
  };

  const buildTeamFromCosts = async () => {
    if (!currentProject || laborItems.length === 0) return;
    const unused = laborItems.filter(item => !item.team_created);
    if (unused.length === 0) return;
    const postResults = await Promise.allSettled(unused.map(item =>
      fetch(`${API_URL}/api/projects/${currentProject.id}/team`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: null, email: null, role: item.role_name || '', allocated_hours: Number(item.hours) })
      }).then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
    ));
    const succeeded = unused.filter((_, i) => postResults[i].status === 'fulfilled');
    if (succeeded.length === 0) return;
    const markResults = await Promise.allSettled(succeeded.map(item =>
      apiFetch(`${API_URL}/api/costs/labor/${item.id}/team-created`, { method: 'PATCH' })
    ));
    const marked = markResults
      .filter(r => r.status === 'fulfilled')
      .map(r => (r as PromiseFulfilledResult<LaborItem>).value);
    if (marked.length > 0) {
      setLaborItems(prev => prev.map(i => {
        const updated = marked.find(m => m.id === i.id);
        return updated ?? i;
      }));
    }
    await loadTeam(currentProject.id);
  };

  const assignTeamResource = async (member: TeamMember, resourceName: string) => {
    const resource = resources.find(r => r.name === resourceName) ?? null;
    try {
      await apiFetch(`${API_URL}/api/team/${member.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: resource ? resource.name : null,
          email: resource ? (resource.email || '') : null,
          allocated_hours: member.allocated_hours
        })
      });
      if (currentProject) await loadTeam(currentProject.id);
    } catch (err: any) { toast.error(errMsg(err, 'Failed to assign player')); }
  };

  const loadRoles = async () => {
    if (!user) return;
    try {
      const data = await apiFetch(`${API_URL}/api/roles/${user.id}`);
      setRoles(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast.error(errMsg(err, 'Failed to load roles'));
    }
  };

  const saveNewRole = async () => {
    if (!user || !newRole.name.trim()) return;
    try {
      const created = await apiFetch(`${API_URL}/api/roles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, name: newRole.name.trim(), hourlyRate: newRole.hourly_rate || null })
      });
      setRoles(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setNewRole({ name: '', hourly_rate: '' });
    } catch (err: any) { toast.error(errMsg(err, 'Failed to save role')); }
  };

  const saveRoleEdit = async () => {
    if (!editingRoleId || !roleDraft.name?.trim()) return;
    try {
      const updated = await apiFetch(`${API_URL}/api/roles/${editingRoleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: roleDraft.name, hourlyRate: roleDraft.hourly_rate || null })
      });
      setRoles(prev => prev.map(r => r.id === editingRoleId ? updated : r).sort((a, b) => a.name.localeCompare(b.name)));
      setEditingRoleId(null);
      setRoleDraft({});
    } catch (err: any) { toast.error(errMsg(err, 'Failed to save role')); }
  };

  const deleteRole = async (id: number) => {
    confirm('Delete this position?', async () => {
      try {
        await apiFetch(`${API_URL}/api/roles/${id}`, { method: 'DELETE' });
        setRoles(prev => prev.filter(r => r.id !== id));
        toast.success('Position deleted');
      } catch (err: any) { toast.error(errMsg(err, 'Failed to delete position')); }
    });
  };

  const loadResources = async () => {
    if (!user) return;
    try {
      const data = await apiFetch(`${API_URL}/api/resources/${user.id}`);
      setResources(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast.error(errMsg(err, 'Failed to load players'));
    }
  };

  const saveNewResource = async () => {
    if (!user || !newResource.name.trim()) return;
    try {
      const created = await apiFetch(`${API_URL}/api/resources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, name: newResource.name.trim(), email: newResource.email, role: newResource.role, hourlyRate: newResource.hourly_rate || null })
      });
      setResources(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setNewResource({ name: '', email: '', role: '', hourly_rate: '' });
    } catch (err: any) { toast.error(errMsg(err, 'Failed to save player')); }
  };

  const saveResourceEdit = async () => {
    if (!editingResourceId || !resourceDraft.name?.trim()) return;
    try {
      const updated = await apiFetch(`${API_URL}/api/resources/${editingResourceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: resourceDraft.name, email: resourceDraft.email, role: resourceDraft.role, hourlyRate: resourceDraft.hourly_rate || null })
      });
      setResources(prev => prev.map(r => r.id === editingResourceId ? updated : r).sort((a, b) => a.name.localeCompare(b.name)));
      setEditingResourceId(null);
      setResourceDraft({});
      if (currentProject) await loadTeam(currentProject.id);
    } catch (err: any) { toast.error(errMsg(err, 'Failed to save player')); }
  };

  const deleteResource = async (id: number) => {
    confirm('Delete this player?', async () => {
      try {
        await apiFetch(`${API_URL}/api/resources/${id}`, { method: 'DELETE' });
        setResources(prev => prev.filter(r => r.id !== id));
        toast.success('Player deleted');
      } catch (err: any) { toast.error(errMsg(err, 'Failed to delete player')); }
    });
  };

  const loadProjects = async () => {
    if (!user) return;
    try {
      const data = await apiFetch(`${API_URL}/api/projects/${user.id}`);
      setProjects(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast.error(errMsg(err, 'Failed to load projects'));
    } finally {
      setProjectsLoaded(true);
    }
  };

  const loadTasks = async (projectId: number) => {
    try {
      const data = await apiFetch(`${API_URL}/api/projects/${projectId}/tasks`);
      setTasks(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast.error(errMsg(err, 'Failed to load tasks'));
    }
  };

  const loadTeam = async (projectId: number) => {
    try {
      const data = await apiFetch(`${API_URL}/api/projects/${projectId}/team`);
      setTeam(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast.error(errMsg(err, 'Failed to load team'));
    }
  };

  const loadActuals = async (projectId: number) => {
    try {
      const data = await apiFetch(`${API_URL}/api/projects/${projectId}/actuals`);
      setActuals(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast.error(errMsg(err, 'Failed to load actuals'));
    }
  };

  const loadBudgetNonLabor = async (projectId: number) => {
    try {
      const data = await apiFetch(`${API_URL}/api/projects/${projectId}/budget-nonlabor`);
      setBudgetNonLabor(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast.error(errMsg(err, 'Failed to load budget items'));
    }
  };

  const addBudgetNL = async () => {
    if (!currentProject || !newBudgetNL.description.trim()) return;
    try {
      const item = await apiFetch(`${API_URL}/api/projects/${currentProject.id}/budget-nonlabor`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: newBudgetNL.description, units: parseFloat(newBudgetNL.units) || 1, rate: parseFloat(newBudgetNL.rate) || 0 })
      });
      setBudgetNonLabor(prev => [...prev, item]);
      setNewBudgetNL({ description: '', units: '', rate: '' });
    } catch (err: any) { toast.error(errMsg(err, 'Failed to add budget item')); }
  };

  const saveBudgetNLEdit = async () => {
    if (!editingBudgetNLId) return;
    try {
      const updated = await apiFetch(`${API_URL}/api/budget-nonlabor/${editingBudgetNLId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: budgetNLDraft.description, units: parseFloat(budgetNLDraft.units) || 1, rate: parseFloat(budgetNLDraft.rate) || 0 })
      });
      setBudgetNonLabor(prev => prev.map(i => i.id === editingBudgetNLId ? updated : i));
      setEditingBudgetNLId(null);
    } catch (err: any) { toast.error(errMsg(err, 'Failed to save budget item')); }
  };

  const deleteBudgetNL = async (id: number) => {
    confirm('Delete this budget item?', async () => {
      try {
        await apiFetch(`${API_URL}/api/budget-nonlabor/${id}`, { method: 'DELETE' });
        setBudgetNonLabor(prev => prev.filter(i => i.id !== id));
        toast.success('Budget item deleted');
      } catch (err: any) { toast.error(errMsg(err, 'Failed to delete budget item')); }
    });
  };

  const saveActual = async (teamMemberId: number, weekStart: string, hoursStr: string) => {
    const hours = hoursStr.trim() === '' ? 0 : parseFloat(hoursStr);
    try {
      const updated = await apiFetch(`${API_URL}/api/team-actuals`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teamMemberId, weekStart, hours })
      });
      const normalize = (s: string) => String(s).substring(0, 10);
      setActuals(prev => {
        const idx = prev.findIndex(a => a.team_member_id === teamMemberId && normalize(a.week_start) === weekStart);
        return idx >= 0 ? prev.map((a, i) => i === idx ? updated : a) : [...prev, updated];
      });
    } catch (err: any) { toast.error(errMsg(err, 'Failed to save actuals')); }
    setEditingActualKey(null);
  };

  const loadReports = async (projectId: number) => {
    try {
      const data = await apiFetch(`${API_URL}/api/projects/${projectId}/reports`);
      setReports(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast.error(errMsg(err, 'Failed to load reports'));
    }
  };

  const sendChat = async () => {
    if (!chatInput.trim() || !currentProject || chatLoading) return;
    const userMsg = { role: 'user' as const, content: chatInput.trim() };
    const updated = [...chatMessages, userMsg];
    setChatMessages(updated);
    setChatInput('');
    setChatLoading(true);
    try {
      const data = await apiFetch(`${API_URL}/api/projects/${currentProject.id}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: updated })
      });
      setChatMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }]);
    }
    setChatLoading(false);
  };

  const sendHelp = async () => {
    if (!helpInput.trim() || helpLoading) return;
    const userMsg = { role: 'user' as const, content: helpInput.trim() };
    const updated = [...helpMessages, userMsg];
    setHelpMessages(updated);
    setHelpInput('');
    setHelpLoading(true);
    try {
      const data = await apiFetch(`${API_URL}/api/help/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: updated })
      });
      setHelpMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch {
      setHelpMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }]);
    }
    setHelpLoading(false);
  };

  const generateReport = async () => {
    if (!currentProject) return;
    setGeneratingReport(true);
    try {
      const report = await apiFetch(`${API_URL}/api/projects/${currentProject.id}/reports`, { method: 'POST' });
      setReports(prev => [report, ...prev]);
      toast.success('Report generated');
    } catch (err: any) {
      toast.error(errMsg(err, 'Failed to generate report'));
    }
    setGeneratingReport(false);
  };

  const saveReport = async (report: StatusReport) => {
    try {
      await apiFetch(`${API_URL}/api/reports/${report.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ full_report: report.full_report })
      });
      setReports(prev => prev.map(r => r.id === report.id ? report : r));
      setEditingReport(null);
    } catch (err: any) { toast.error(errMsg(err, 'Failed to save report')); }
  };

  const generatePlan = async () => {
    if (!description.trim()) return;
    setLoading(true);
    setPlan(null);
    try {
      const data = await apiFetch(`${API_URL}/api/generate-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description })
      });
      setPlan(data);
      setPlanOrigin('new');
      setView('plan');
    } catch (err) {
      toast.error('Something went wrong. Make sure the backend is running.');
    }
    setLoading(false);
  };

  const savePlan = async () => {
    if (!plan || !user) return;
    setSaving(true);
    try {
      const saved = await apiFetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, name: plan.projectName, description, plan, startDate: planStartDate || null, includeWeekends: settings.include_weekends })
      });
      await loadProjects();
      setSaving(false);
      setCurrentProject(saved);
      await Promise.all([loadTasks(saved.id), loadTeam(saved.id), loadReports(saved.id)]);
      toast.success('Project saved');
      setView('tasks');
      setPlan(null);
      setDescription('');
      setPlanStartDate('');
    } catch (err: any) {
      setSaving(false);
      if (err?.status === 403) {
        setShowUpgradeModal(true);
      } else {
        toast.error(errMsg(err, 'Failed to save project.'));
      }
    }
  };

  const deleteProject = async (id: number) => {
    confirm('Are you sure you want to delete this project and all its tasks?', async () => {
      try {
        await apiFetch(`${API_URL}/api/projects/${id}`, { method: 'DELETE' });
        await loadProjects();
        toast.success('Project deleted');
      } catch (err: any) { toast.error(errMsg(err, 'Failed to delete project')); }
    });
  };

  const runAutoRag = async (projectId: number) => {
    try {
      const updated: { id: number; rag_status: string | null }[] = await apiFetch(`${API_URL}/api/projects/${projectId}/auto-rag`, { method: 'POST' });
      if (updated.length > 0) {
        setTasks(prev => prev.map(t => {
          const u = updated.find(u => u.id === t.id);
          return u ? { ...t, rag_status: u.rag_status } : t;
        }));
      }
    } catch (err: any) { toast.error(errMsg(err, 'Failed to run auto-RAG')); }
  };

  const openProject = async (project: Project) => {
    try {
      const [taskData, teamData, reportData, costData, actualData, budgetData] = await Promise.all([
        apiFetch(`${API_URL}/api/projects/${project.id}/tasks`),
        apiFetch(`${API_URL}/api/projects/${project.id}/team`),
        apiFetch(`${API_URL}/api/projects/${project.id}/reports`),
        apiFetch(`${API_URL}/api/projects/${project.id}/costs`),
        apiFetch(`${API_URL}/api/projects/${project.id}/actuals`),
        apiFetch(`${API_URL}/api/projects/${project.id}/budget-nonlabor`),
      ]);
      setTasks(Array.isArray(taskData) ? taskData : []);
      setTeam(Array.isArray(teamData) ? teamData : []);
      setReports(Array.isArray(reportData) ? reportData : []);
      setLaborItems(costData.labor || []);
      setNonLaborItems(costData.nonlabor || []);
      setActuals(Array.isArray(actualData) ? actualData : []);
      setBudgetNonLabor(Array.isArray(budgetData) ? budgetData : []);
      setCurrentProject(project);
      if (settings.auto_rag) await runAutoRag(project.id);
      setView('tasks');
    } catch (err: any) {
      toast.error(errMsg(err, 'Failed to open project'));
    }
  };

  const copyProject = async (project: Project) => {
    let liveTasks: Task[];
    try {
      liveTasks = await apiFetch(`${API_URL}/api/projects/${project.id}/tasks`);
    } catch (err: any) {
      toast.error(errMsg(err, 'Failed to copy project'));
      return;
    }

    // Build DB id -> 1-based flat index map
    const idToFlatIndex = new Map<number, number>();
    liveTasks.forEach((task, i) => idToFlatIndex.set(task.id, i + 1));

    const phaseMap = new Map<string, any[]>();
    for (const task of liveTasks) {
      if (!phaseMap.has(task.phase_name)) phaseMap.set(task.phase_name, []);
      phaseMap.get(task.phase_name)!.push({
        name: task.name,
        description: task.description,
        estimatedDays: task.estimated_days,
        priority: task.priority,
        predecessors: task.depends_on_ids.map(id => idToFlatIndex.get(id)).filter(Boolean)
      });
    }
    const phases = Array.from(phaseMap.entries()).map(([name, tasks]) => ({ name, tasks }));
    setPlan({ projectName: `(Copy of) ${project.name}`, phases });
    setPlanStartDate('');
    setDescription('');
    setPlanOrigin('dashboard');
    setView('plan');
  };
const renameProject = (project: Project) => {
    setPromptModal({
      label: 'Rename project:',
      value: project.name,
      onSubmit: async (newName) => {
        try {
          await apiFetch(`${API_URL}/api/projects/${project.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: newName })
          });
          await loadProjects();
          if (currentProject?.id === project.id) {
            setCurrentProject({ ...currentProject, name: newName });
          }
        } catch (err: any) { toast.error(errMsg(err, 'Failed to rename project')); }
      }
    });
  };
  const computeCascade = (changedTaskId: number, oldEnd: string, newEnd: string, allTasks: Task[]) => {
    if (!oldEnd || !newEnd || oldEnd === newEnd) return [];
    const includeWeekends = settings.include_weekends;
    const dependentsOf = new Map<number, number[]>();
    for (const t of allTasks) {
      for (const predId of t.depends_on_ids) {
        if (!dependentsOf.has(predId)) dependentsOf.set(predId, []);
        dependentsOf.get(predId)!.push(t.id);
      }
    }
    const taskMap = new Map<number, Task>(allTasks.map(t => [t.id, t]));
    const updatedEndDates = new Map<number, string>();
    updatedEndDates.set(changedTaskId, newEnd);
    const changes: { taskId: number; name: string; newStart: string; newEnd: string; estimatedDays: number }[] = [];
    const visited = new Set<number>();
    const queue = [changedTaskId];
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      for (const depId of (dependentsOf.get(currentId) || [])) {
        if (visited.has(depId)) continue;
        const dep = taskMap.get(depId);
        if (!dep || !dep.start_date || !dep.end_date) continue;
        // Find max end date among all this task's predecessors
        let maxPredEnd = '';
        for (const predId of dep.depends_on_ids) {
          const predEnd = updatedEndDates.get(predId) || taskMap.get(predId)?.end_date || '';
          const predEndStr = predEnd ? predEnd.substring(0, 10) : '';
          if (predEndStr > maxPredEnd) maxPredEnd = predEndStr;
        }
        if (!maxPredEnd) continue;
        const newDepStart = advanceDateJS(maxPredEnd, 1, includeWeekends);
        const currentDepStart = dep.start_date.substring(0, 10);
        if (newDepStart === currentDepStart) continue; // not the driver predecessor
        const delta = workingDayDelta(currentDepStart, newDepStart, includeWeekends);
        const newDepEnd = advanceDateJS(dep.end_date.substring(0, 10), delta, includeWeekends);
        updatedEndDates.set(depId, newDepEnd);
        visited.add(depId);
        changes.push({ taskId: depId, name: dep.name, newStart: newDepStart, newEnd: newDepEnd, estimatedDays: dep.estimated_days });
        queue.push(depId);
      }
    }
    return changes;
  };

  const applyBatchDates = async (updates: { taskId: number; newStart: string; newEnd: string; estimatedDays: number }[]) => {
    try {
      await apiFetch(`${API_URL}/api/tasks/batch-dates`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates.map(u => ({ id: u.taskId, start_date: u.newStart, end_date: u.newEnd, estimated_days: u.estimatedDays })) })
      });
      setTasks(prev => prev.map(t => {
        const u = updates.find(u => u.taskId === t.id);
        return u ? { ...t, start_date: u.newStart, end_date: u.newEnd, estimated_days: u.estimatedDays } : t;
      }));
    } catch (err: any) { toast.error(errMsg(err, 'Failed to update dates')); }
  };

  const updateTask = async (task: Task) => {
    const doSave = async () => {
      try {
        await apiFetch(`${API_URL}/api/tasks/${task.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: task.name, description: task.description, status: task.status, assignee_id: task.assignee_id, start_date: task.start_date, end_date: task.end_date, priority: task.priority })
        });
        if (currentProject) await loadTasks(currentProject.id);
        setEditingTask(null);
      } catch (err: any) { toast.error(errMsg(err, 'Failed to save task')); }
    };
    const original = tasks.find(t => t.id === task.id);
    const oldEnd = original?.end_date?.substring(0, 10) || '';
    const newEnd = task.end_date?.substring(0, 10) || '';
    const cascade = computeCascade(task.id, oldEnd, newEnd, tasks);
    if (cascade.length > 0) {
      setEditingTask(null);
      setCascadeModal({ cascadeChanges: cascade, onSaveOnly: doSave, onSaveWithCascade: async () => { await doSave(); await applyBatchDates(cascade); } });
    } else {
      await doSave();
    }
  };

  const updateTaskField = async (task: Task, field: keyof Task, value: any) => {
    const updated: Task = { ...task, [field]: value };
    if (field === 'assignee_id') {
      const member = team.find(m => m.id === Number(value));
      updated.assignee_name = member ? member.name : null;
      updated.assignee_id = value ? Number(value) : null;
    }
    if (field === 'start_date' || field === 'end_date') {
      const start = updated.start_date ? updated.start_date.substring(0, 10) : null;
      const end = updated.end_date ? updated.end_date.substring(0, 10) : null;
      if (start && end && start > end) { setInlineEdit(null); return; }
      if (start && end) {
        updated.estimated_days = Math.round((new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60 * 60 * 24)) + 1;
      }
    }
    if (field === 'rag_status') updated.rag_manual = value !== null && value !== '';
    setTasks(prev => prev.map(t => t.id === task.id ? updated : t));
    setInlineEdit(null);
    const doSave = async () => {
      try {
        await apiFetch(`${API_URL}/api/tasks/${task.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: updated.name, description: updated.description, status: updated.status, assignee_id: updated.assignee_id, start_date: updated.start_date, end_date: updated.end_date, priority: updated.priority, estimated_days: updated.estimated_days, rag_status: updated.rag_status, progress_notes: updated.progress_notes, percent_complete: updated.percent_complete, rag_manual: updated.rag_manual })
        });
      } catch (err: any) { toast.error(errMsg(err, 'Failed to save task')); }
    };
    if (field === 'end_date') {
      const oldEnd = task.end_date?.substring(0, 10) || '';
      const newEnd = (value as string)?.substring(0, 10) || '';
      const cascade = computeCascade(task.id, oldEnd, newEnd, tasks);
      if (cascade.length > 0) {
        setCascadeModal({ cascadeChanges: cascade, onSaveOnly: doSave, onSaveWithCascade: async () => { await doSave(); await applyBatchDates(cascade); } });
        return;
      }
    }
    await doSave();
  };

  const updateTaskDates = async (taskId: number, startDate: string, endDate: string) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    const estimatedDays = Math.round((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const updated = { ...task, start_date: startDate, end_date: endDate, estimated_days: estimatedDays };
    setTasks(prev => prev.map(t => t.id === taskId ? updated : t));
    const doSave = async () => {
      try {
        await apiFetch(`${API_URL}/api/tasks/${taskId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: updated.name, description: updated.description, status: updated.status, assignee_id: updated.assignee_id, start_date: startDate, end_date: endDate, priority: updated.priority, estimated_days: estimatedDays, rag_status: updated.rag_status, progress_notes: updated.progress_notes, percent_complete: updated.percent_complete })
        });
      } catch (err: any) { toast.error(errMsg(err, 'Failed to save task dates')); }
    };
    const oldEnd = task.end_date?.substring(0, 10) || '';
    const newEnd = endDate.substring(0, 10);
    const cascade = computeCascade(taskId, oldEnd, newEnd, tasks);
    if (cascade.length > 0) {
      setCascadeModal({ cascadeChanges: cascade, onSaveOnly: doSave, onSaveWithCascade: async () => { await doSave(); await applyBatchDates(cascade); } });
    } else {
      await doSave();
    }
  };

  const updateDependencies = async (taskId: number, dependsOnIds: number[]) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, depends_on_ids: dependsOnIds } : t));
    try {
      await apiFetch(`${API_URL}/api/tasks/${taskId}/dependencies`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dependsOnIds })
      });
    } catch (err: any) { toast.error(errMsg(err, 'Failed to update dependencies')); }
  };

  const captureGantt = async () => {
    if (!ganttRef.current) return null;
    const html2canvas = (await import('html2canvas')).default;

    // Measure the full inner content width (may be wider than the viewport)
    const innerEl = ganttRef.current.querySelector('[data-gantt-inner]') as HTMLElement | null;
    const fullWidth = innerEl ? innerEl.scrollWidth : ganttRef.current.scrollWidth;

    // Stamp measured heights onto overflow-hidden elements before cloning
    const stamped: HTMLElement[] = [];
    ganttRef.current.querySelectorAll('.overflow-hidden').forEach(el => {
      const h = el as HTMLElement;
      h.dataset.captureH = String(h.offsetHeight);
      stamped.push(h);
    });

    const canvas = await html2canvas(ganttRef.current, {
      scale: 2,
      useCORS: true,
      logging: false,
      width: fullWidth,
      windowWidth: fullWidth,
      onclone: (_doc, clonedEl) => {
        // Expand outer wrapper and scroll container to full content width
        clonedEl.style.width = `${fullWidth}px`;
        const scrollEl = clonedEl.querySelector('[data-gantt-scroll]') as HTMLElement | null;
        if (scrollEl) {
          scrollEl.style.maxHeight = 'none';
          scrollEl.style.overflow = 'visible';
          scrollEl.style.width = `${fullWidth}px`;
        }
        // Fix overflow-hidden containers: remove clip but preserve measured height
        clonedEl.querySelectorAll('[data-capture-h]').forEach(node => {
          const el = node as HTMLElement;
          el.style.overflow = 'visible';
          el.style.borderRadius = '0';
          if (el.dataset.captureH) el.style.height = `${el.dataset.captureH}px`;
        });
        // Fix truncate spans: allow vertical overflow so text isn't clipped
        clonedEl.querySelectorAll('.truncate').forEach(node => {
          (node as HTMLElement).style.overflow = 'visible';
        });
      }
    });

    // Clean up stamped attributes
    stamped.forEach(el => delete el.dataset.captureH);

    return canvas;
  };

  const exportGanttPng = async () => {
    if (!currentProject) return;
    const canvas = await captureGantt();
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `${currentProject.name}-gantt.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    setShowGanttExportMenu(false);
  };

  const exportGanttPdf = async () => {
    if (!currentProject) return;
    const canvas = await captureGantt();
    if (!canvas) return;
    const { jsPDF } = await import('jspdf');
    const w = canvas.width / 2;
    const h = canvas.height / 2;
    const pdf = new jsPDF({ orientation: w > h ? 'landscape' : 'portrait', unit: 'px', format: [w, h] });
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, w, h);
    pdf.save(`${currentProject.name}-gantt.pdf`);
    setShowGanttExportMenu(false);
  };

  const movePhase = (phase: string, direction: 'up' | 'down') => {
    const phaseList = Array.from(new Set(tasks.map(t => t.phase_name)));
    const idx = phaseList.indexOf(phase);
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= phaseList.length) return;
    const newPhases = [...phaseList];
    [newPhases[idx], newPhases[newIdx]] = [newPhases[newIdx], newPhases[idx]];
    setTasks(prev => {
      const result: Task[] = [];
      for (const p of newPhases) {
        result.push(...prev.filter(t => t.phase_name === p).map(t => ({ ...t, phase_order: newPhases.indexOf(p) })));
      }
      return result;
    });
    if (currentProject) {
      apiFetch(`${API_URL}/api/projects/${currentProject.id}/phases/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phases: newPhases })
      }).catch((err: any) => console.error('Phase reorder failed:', err));
    }
  };

  const reorderTasks = async (phase: string, orderedIds: number[]) => {
    setTasks(prev => {
      const taskMap = new Map(prev.map(t => [t.id, t]));
      const reorderedPhase = orderedIds.map((id, i) => ({ ...taskMap.get(id)!, task_order: i }));
      let inserted = false;
      const result: Task[] = [];
      for (const t of prev) {
        if (t.phase_name === phase) {
          if (!inserted) { result.push(...reorderedPhase); inserted = true; }
        } else {
          result.push(t);
        }
      }
      return result;
    });
    if (currentProject) {
      try {
        await apiFetch(`${API_URL}/api/projects/${currentProject.id}/tasks/reorder`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskIds: orderedIds })
        });
      } catch (err: any) { console.error('Task reorder failed:', err); }
    }
  };

  const deleteTask = async (id: number) => {
    confirm('Are you sure you want to delete this task?', async () => {
      try {
        await apiFetch(`${API_URL}/api/tasks/${id}`, { method: 'DELETE' });
        if (currentProject) await loadTasks(currentProject.id);
        toast.success('Task deleted');
      } catch (err: any) { toast.error(errMsg(err, 'Failed to delete task')); }
    });
  };

  const addTask = async (phaseName: string) => {
    if (!currentProject) return;
    try {
      const created = await apiFetch(`${API_URL}/api/projects/${currentProject.id}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phase_name: phaseName,
          name: 'New Task',
          description: 'Task description',
          estimated_days: 1,
          priority: 'Medium'
        })
      });
      setTasks(prev => {
        const lastPhaseIdx = prev.reduce((acc, t, i) => t.phase_name === phaseName ? i : acc, -1);
        const next = [...prev];
        next.splice(lastPhaseIdx + 1, 0, { ...created, depends_on_ids: [] });
        return next;
      });
      setNewTaskId(created.id);
    } catch (err: any) { toast.error(errMsg(err, 'Failed to add task')); }
  };
const updatePhaseName = async (oldName: string, newName: string) => {
    if (!currentProject || !newName.trim()) return;
    try {
      await apiFetch(`${API_URL}/api/projects/${currentProject.id}/phases`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ oldName, newName })
      });
      await loadTasks(currentProject.id);
    } catch (err: any) { toast.error(errMsg(err, 'Failed to rename phase')); }
  };

  const deletePhase = async (phaseName: string) => {
    if (!currentProject) return;
    confirm(`Delete phase "${phaseName}" and all its tasks?`, async () => {
      try {
        await apiFetch(`${API_URL}/api/projects/${currentProject.id}/phases`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phaseName })
        });
        await loadTasks(currentProject.id);
        toast.success('Phase deleted');
      } catch (err: any) { toast.error(errMsg(err, 'Failed to delete phase')); }
    });
  };

  const addPhase = () => {
    if (!currentProject) return;
    setPromptModal({
      label: 'New phase name',
      value: '',
      onSubmit: async (phaseName) => {
        try {
          await apiFetch(`${API_URL}/api/projects/${currentProject.id}/phases`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ phaseName })
          });
          await loadTasks(currentProject.id);
        } catch (err: any) { toast.error(errMsg(err, 'Failed to add phase')); }
      }
    });
  };

  const insertPhase = (relativePhase: string, position: 'above' | 'below') => {
    if (!currentProject) return;
    setPromptModal({
      label: `Insert new phase ${position} "${relativePhase}":`,
      value: '',
      onSubmit: async (phaseName) => {
        try {
          await apiFetch(`${API_URL}/api/projects/${currentProject.id}/phases`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ phaseName })
          });
          const currentPhases = Array.from(new Set(tasks.map(t => t.phase_name)));
          const idx = currentPhases.indexOf(relativePhase);
          const insertIdx = position === 'above' ? idx : idx + 1;
          const newOrder = [...currentPhases.slice(0, insertIdx), phaseName, ...currentPhases.slice(insertIdx)];
          await apiFetch(`${API_URL}/api/projects/${currentProject.id}/phases/reorder`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ phases: newOrder })
          });
          await loadTasks(currentProject.id);
        } catch (err: any) { toast.error(errMsg(err, 'Failed to insert phase')); }
      }
    });
  };

  const addTeamMember = async () => {
    if (!newMember.name || !currentProject) return;
    try {
      await apiFetch(`${API_URL}/api/projects/${currentProject.id}/team`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newMember, allocated_hours: newMember.allocated_hours !== '' ? parseFloat(newMember.allocated_hours) : null })
      });
      await loadTeam(currentProject.id);
      setNewMember({ name: '', email: '', role: '', allocated_hours: '' });
      setAddingMember(false);
    } catch (err: any) { toast.error(errMsg(err, 'Failed to add team member')); }
  };

  const saveMemberHours = async (member: TeamMember, hours: string) => {
    const parsed = hours.trim() === '' ? null : parseFloat(hours);
    try {
      await apiFetch(`${API_URL}/api/team/${member.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: member.name, email: member.email, allocated_hours: parsed })
      });
      if (currentProject) await loadTeam(currentProject.id);
      setEditingMemberId(null);
    } catch (err: any) { toast.error(errMsg(err, 'Failed to save hours')); }
  };

  const deleteTeamMember = async (id: number) => {
    confirm('Remove this team member?', async () => {
      try {
        await apiFetch(`${API_URL}/api/team/${id}`, { method: 'DELETE' });
        if (currentProject) await loadTeam(currentProject.id);
        toast.success('Team member removed');
      } catch (err: any) { toast.error(errMsg(err, 'Failed to remove team member')); }
    });
  };

  const statusColor = (status: string) => {
    if (status === 'Complete') return 'bg-green-100 text-green-600';
    if (status === 'In Progress') return 'bg-blue-100 text-blue-600';
    if (status === 'Blocked') return 'bg-red-100 text-red-600';
    return 'bg-gray-100 text-gray-600';
  };

  const priorityColor = (priority: string) => {
    if (priority === 'High') return 'bg-red-100 text-red-600';
    if (priority === 'Medium') return 'bg-yellow-100 text-yellow-600';
    return 'bg-green-100 text-green-600';
  };

const confirm = (message: string, action: () => void) => {
    setConfirmMessage(message);
    setConfirmAction(() => action);
  };

  const phases = Array.from(new Set(tasks.map(t => t.phase_name)));
  const taskNumbers = new Map(tasks.map((t, i) => [t.id, i + 1]));

  return (
    <div className="h-screen flex flex-col bg-gray-100 overflow-hidden">

      <Toaster position="top-center" toastOptions={{ duration: 6000 }} />

      {/* Header */}
      <header className="bg-primary text-white px-8 py-4 shadow-md flex justify-between items-center">
        <div className="cursor-pointer" onClick={() => setView('dashboard')}>
          <img src={logo} alt="gAImplans" className="h-16 w-auto" />
        </div>
        <div>
          <SignedOut>
            <div className="flex items-center gap-4">
              <a href="/user-guide.html" target="_blank" rel="noopener noreferrer" className="text-sm font-semibold opacity-80 hover:opacity-100 transition-opacity">User Guide</a>
              <SignInButton mode="modal">
                <button className="bg-white text-primary font-semibold px-5 py-2 rounded-xl hover:bg-accent hover:text-white transition-colors">Sign In</button>
              </SignInButton>
            </div>
          </SignedOut>
          <SignedIn>
            <div className="flex items-center gap-4">
              <a href="/user-guide.html" target="_blank" rel="noopener noreferrer" className="text-sm font-semibold opacity-80 hover:opacity-100 transition-colors">User Guide</a>
              <button
                onClick={() => setFeedbackOpen(true)}
                className="text-sm font-semibold px-4 py-1.5 rounded-xl border border-white border-opacity-50 hover:bg-white hover:text-primary transition-colors"
              >
                Feedback
              </button>
              {(userTier === 'pro' || userTier === 'unlimited') ? (
                <button
                  onClick={openPortal}
                  className="text-sm font-semibold px-4 py-1.5 rounded-xl border border-white border-opacity-50 hover:bg-white hover:text-primary transition-colors"
                >
                  Manage Subscription
                </button>
              ) : (
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="text-sm font-bold px-4 py-1.5 rounded-xl transition-colors"
                  style={{ backgroundColor: '#9FE1CB', color: '#3C3489' }}
                >
                  Upgrade
                </button>
              )}
              <span className="text-sm opacity-80">Welcome, {user?.firstName}!</span>
              <UserButton afterSignOutUrl="/" />
            </div>
          </SignedIn>
        </div>
      </header>

      {/* Project tab bar */}
      {(view === 'tasks' || view === 'gantt' || view === 'team' || view === 'reports' || view === 'costs' || view === 'budget') && currentProject && (
        <nav className="bg-white border-b border-gray-200 px-8 shadow-sm">
          <div className="flex items-center">
            <button
              onClick={() => setView('dashboard')}
              className="text-primary hover:text-accent font-semibold text-sm py-3 pr-6 border-b-2 border-transparent">
              ← Back
            </button>
            <button
              onClick={() => setView('tasks')}
              className={`px-6 py-3 font-semibold text-sm border-b-2 transition-colors ${view === 'tasks' ? 'border-primary text-primary' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
              Schedule
            </button>
            <button
              onClick={() => setView('gantt')}
              className={`px-6 py-3 font-semibold text-sm border-b-2 transition-colors ${view === 'gantt' ? 'border-primary text-primary' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
              Gantt
            </button>
            <button
              onClick={() => setView('team')}
              className={`px-6 py-3 font-semibold text-sm border-b-2 transition-colors ${view === 'team' ? 'border-primary text-primary' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
              Team
            </button>
            <button
              onClick={() => { setView('costs'); if (currentProject) loadCosts(currentProject.id); }}
              className={`px-6 py-3 font-semibold text-sm border-b-2 transition-colors ${view === 'costs' ? 'border-primary text-primary' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
              Cost Plan
            </button>
            <button
              onClick={() => setView('budget')}
              className={`px-6 py-3 font-semibold text-sm border-b-2 transition-colors ${view === 'budget' ? 'border-primary text-primary' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
              Budget / Actuals
            </button>
            <button
              onClick={() => setView('reports')}
              className={`px-6 py-3 font-semibold text-sm border-b-2 transition-colors ${view === 'reports' ? 'border-primary text-primary' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
              Status Reports
            </button>
          </div>
        </nav>
      )}

      <main className="flex-1 overflow-y-auto">

        <SignedOut>

          {/* Notice banner (currently empty, reserved for future announcements) */}
          <div className="w-full text-center py-2 text-sm font-semibold text-white" style={{backgroundColor: '#9FE1CB', color: '#3C3489'}}>
          </div>

          {/* Landing Nav */}
          <nav className="sticky top-0 z-40 bg-white border-b border-gray-100 shadow-sm px-6 py-4 flex items-center justify-between">
            <div className="hidden md:flex items-center gap-8 text-sm font-semibold text-gray-500">
              <button onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-primary transition-colors">Features</button>
              <button onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-primary transition-colors">Pricing</button>
              <SignInButton mode="modal">
                <button className="hover:text-primary transition-colors">Sign In</button>
              </SignInButton>
            </div>
            <SignInButton mode="modal">
              <button className="font-bold px-5 py-2 rounded-xl text-sm transition-colors" style={{backgroundColor: '#9FE1CB', color: '#3C3489'}}>Start free trial</button>
            </SignInButton>
          </nav>

          {/* Hero */}
          <section style={{background: 'linear-gradient(160deg, #3C3489 0%, #534AB7 100%)'}} className="text-white pt-24 pb-16 px-6 text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-5xl font-extrabold mb-5 leading-tight tracking-tight">Project plans in seconds,<br/>not hours</h2>
              <p className="text-lg mb-10 max-w-xl mx-auto" style={{opacity: 0.75}}>AI-generated phase-by-phase plans, live RAG status tracking, dependency-aware scheduling, and an AI Coach — all in one place.</p>
              <div className="flex gap-4 justify-center flex-wrap mb-5">
                <SignInButton mode="modal">
                  <button className="font-bold px-8 py-3 rounded-xl transition-colors text-sm" style={{backgroundColor: '#9FE1CB', color: '#3C3489'}}>Start free trial →</button>
                </SignInButton>
                <a href="#how-it-works" className="font-semibold px-8 py-3 rounded-xl text-sm transition-colors" style={{border: '1px solid rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.85)'}}>See how it works</a>
              </div>
              <p className="text-xs" style={{opacity: 0.45}}>14-day free trial · No credit card required</p>
            </div>

            {/* Screenshot fan */}
            <div className="relative mx-auto mt-16 mb-8" style={{maxWidth: '800px', height: '420px'}}>
              {/* Left screenshot */}
              <img src="/screenshot-left.png" alt=""
                className="absolute rounded-xl shadow-2xl"
                style={{width: '62%', left: '0%', top: '8%', transform: 'rotate(-5deg)', zIndex: 1, opacity: 0.85}} />
              {/* Right screenshot */}
              <img src="/screenshot-right.png" alt=""
                className="absolute rounded-xl shadow-2xl"
                style={{width: '62%', right: '0%', top: '8%', transform: 'rotate(5deg)', zIndex: 1, opacity: 0.85}} />
              {/* Center screenshot — front */}
              <img src="/screenshot-center.png" alt=""
                className="absolute rounded-xl shadow-2xl"
                style={{width: '68%', left: '16%', top: '0%', zIndex: 2}} />
            </div>
            <div className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full mt-8" style={{backgroundColor: 'rgba(159,225,203,0.15)', color: '#9FE1CB', border: '1px solid rgba(159,225,203,0.35)'}}>
              <span className="w-1.5 h-1.5 rounded-full bg-current"></span> Powered by Claude AI
            </div>
          </section>

          {/* Features */}
          <section id="features" className="py-20 px-6 bg-white">
            <div className="max-w-5xl mx-auto">
              <h3 className="text-3xl font-extrabold text-center mb-3" style={{color: '#3C3489'}}>Everything your project needs</h3>
              <p className="text-center text-gray-400 mb-12 text-sm">From first idea to final delivery — all in one place.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { icon: '⚡', title: 'AI Plan Generation', badge: null, desc: 'Describe your project and get a complete phase-by-phase plan with tasks, durations, dependencies, and priorities in seconds.' },
                  { icon: '💬', title: 'AI Coach', badge: 'New', desc: 'Ask your built-in AI coach anything about your project — risks, workload, blockers, next steps — and get instant answers.' },
                  { icon: '🟢', title: 'RAG Status Automation', badge: 'New', desc: 'Automatically sets Red/Amber/Green health on every task by comparing % complete to % of duration elapsed. No manual updates needed.' },
                  { icon: '📊', title: 'Gantt with Dependencies', badge: null, desc: 'Interactive Gantt chart with drag-to-reschedule. Predecessor-aware date cascading keeps dependent tasks in sync automatically.' },
                  { icon: '📋', title: 'AI Status Reports', badge: 'New', desc: 'Generate polished status reports with one click. Download as Word documents to share with stakeholders.' },
                  { icon: '📤', title: 'Export Anywhere', badge: null, desc: 'Export your full project plan to Excel, Gantt to PDF, or status reports to Word — ready to share in seconds.' },
                ].map(f => (
                  <div key={f.title} className="rounded-2xl p-6 border border-gray-100 hover:shadow-md transition-shadow" style={{backgroundColor: '#FAFAFE'}}>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-2xl">{f.icon}</span>
                      <span className="font-bold text-base" style={{color: '#3C3489'}}>{f.title}</span>
                      {f.badge && <span className="text-xs font-bold px-2 py-0.5 rounded-full ml-auto" style={{backgroundColor: '#9FE1CB', color: '#3C3489'}}>{f.badge}</span>}
                    </div>
                    <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* How it works */}
          <section id="how-it-works" className="py-20 px-6" style={{backgroundColor: '#EEEDFE'}}>
            <div className="max-w-4xl mx-auto">
              <h3 className="text-3xl font-extrabold text-center mb-3" style={{color: '#3C3489'}}>How it works</h3>
              <p className="text-center text-gray-400 mb-14 text-sm">Four steps from idea to fully tracked, reportable project.</p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                {[
                  { step: '1', title: 'Describe your project', desc: 'Type a plain-English description. No templates, no forms — just your words.' },
                  { step: '2', title: 'AI builds your plan', desc: 'Claude AI generates phases, tasks, durations, priorities, and predecessor links tailored to your project.' },
                  { step: '3', title: 'Assign and track', desc: 'Add your team, assign tasks, set dates, and monitor RAG health from a live schedule and Gantt chart.' },
                  { step: '4', title: 'Report in seconds', desc: 'Generate an AI status report, download as Word, and share with stakeholders — all in one click.' },
                ].map((s, i) => (
                  <div key={s.step} className="text-center relative">
                    {i < 3 && <div className="hidden md:block absolute top-7 left-1/2 w-full h-0.5 opacity-20" style={{backgroundColor: '#3C3489'}}></div>}
                    <div className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-extrabold text-white mx-auto mb-4 relative z-10" style={{backgroundColor: '#3C3489'}}>{s.step}</div>
                    <h4 className="font-bold text-sm mb-2" style={{color: '#3C3489'}}>{s.title}</h4>
                    <p className="text-gray-500 text-xs leading-relaxed">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Pricing */}
          <section id="pricing" className="py-20 px-6 bg-white">
            <div className="max-w-5xl mx-auto">
              <h3 className="text-3xl font-extrabold text-center mb-3" style={{color: '#3C3489'}}>Simple, honest pricing</h3>
              <p className="text-center text-gray-400 mb-12 text-sm">Start free. Scale when you're ready.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start max-w-5xl mx-auto">
                {[
                  { name: 'Starter', price: 'Free', period: '14 days', badge: null, cta: 'Start free trial',
                    features: ['Up to 3 projects', 'Unlimited team members', 'AI plan generation', 'Task tracking & RAG status', 'Gantt chart with dependencies', 'Export to Excel & PDF'] },
                  { name: 'Pro', price: '$29', originalPrice: '$39', launchLabel: 'Launch Pricing', period: '/month', badge: 'Most popular', cta: 'Get started',
                    features: ['Up to 10 projects', 'Unlimited team members', 'All Starter features', 'AI Coach assistant', 'AI status reports', 'Export to Excel, Word & PDF'] },
                  { name: 'Unlimited', price: '$59', period: '/month', badge: null, cta: 'Get started',
                    features: ['Unlimited projects', 'Unlimited team members', 'All Pro features', 'AI Coach assistant', 'AI status reports', 'Export to Excel, Word & PDF'] },
                ].map(tier => (
                  <div key={tier.name} className={`rounded-2xl p-8 ${tier.badge ? 'shadow-2xl' : 'border border-gray-100 shadow-sm'}`}
                    style={tier.badge ? {backgroundColor: '#3C3489', color: 'white'} : {backgroundColor: '#FAFAFE'}}>
                    {tier.badge && (
                      <div className="text-xs font-bold px-3 py-1 rounded-full inline-block mb-4" style={{backgroundColor: '#9FE1CB', color: '#3C3489'}}>{tier.badge}</div>
                    )}
                    <h4 className="text-xl font-extrabold mb-1" style={!tier.badge ? {color: '#3C3489'} : {}}>{tier.name}</h4>
                    <div className="flex items-end gap-2 mb-2 mt-2 flex-wrap">
                      {(tier as any).originalPrice && (
                        <span className="text-2xl font-extrabold line-through opacity-50">{(tier as any).originalPrice}</span>
                      )}
                      <span className="text-4xl font-extrabold" style={(tier as any).originalPrice ? {color: '#ef4444'} : {}}>{tier.price}</span>
                      <span className={`text-sm pb-1.5 ${tier.badge ? 'opacity-60' : 'text-gray-400'}`}>{tier.period}</span>
                    </div>
                    {(tier as any).launchLabel && (
                      <p className="text-xs font-semibold mb-4" style={{color: '#9FE1CB'}}>{(tier as any).launchLabel}</p>
                    )}
                    <ul className="space-y-3 mb-8">
                      {tier.features.map(f => (
                        <li key={f} className={`flex items-start gap-2 text-sm ${tier.badge ? 'opacity-85' : 'text-gray-600'}`}>
                          <span className="font-bold mt-px shrink-0" style={{color: tier.badge ? '#9FE1CB' : '#3C3489'}}>✓</span>{f}
                        </li>
                      ))}
                    </ul>
                    <SignInButton mode="modal">
                      <button className="w-full font-bold py-3 rounded-xl text-sm transition-colors"
                        style={tier.badge ? {backgroundColor: '#9FE1CB', color: '#3C3489'} : {backgroundColor: '#3C3489', color: 'white'}}>
                        {tier.cta}
                      </button>
                    </SignInButton>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Final CTA */}
          <section className="py-24 px-6 text-white text-center" style={{background: 'linear-gradient(160deg, #3C3489 0%, #534AB7 100%)'}}>
            <div className="max-w-2xl mx-auto">
              <h3 className="text-4xl font-extrabold mb-4 tracking-tight">Ready to plan smarter?</h3>
              <p className="mb-8 text-lg" style={{opacity: 0.75}}>Join teams using AI to cut planning time from hours to seconds.</p>
              <SignInButton mode="modal">
                <button className="font-bold px-10 py-4 rounded-xl text-base transition-colors" style={{backgroundColor: '#9FE1CB', color: '#3C3489'}}>Start your free trial →</button>
              </SignInButton>
              <p className="mt-4 text-xs" style={{opacity: 0.4}}>14-day free trial · No credit card required</p>
            </div>
          </section>

          {/* Footer */}
          <footer className="py-8 px-6 bg-white border-t border-gray-100">
            <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
              <span className="text-sm font-extrabold" style={{color: '#3C3489'}}>g<span style={{color: '#9FE1CB'}}>AI</span>mplans</span>
              <p className="text-xs text-gray-400">© {new Date().getFullYear()} Go Big Red LLC. All rights reserved.</p>
              <div className="flex gap-6 text-xs text-gray-400">
                <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Privacy</a>
                <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Terms</a>
                <a href="mailto:support@gaimplans.com" className="hover:text-primary transition-colors">Contact</a>
              </div>
            </div>
          </footer>

        </SignedOut>

        <div className="px-6 py-10">
        <SignedIn>

          {/* Dashboard */}
          {view === 'dashboard' && (
            <div className="max-w-5xl mx-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-primary">My Projects</h2>
                <div className="flex gap-3 flex-wrap">
                  <button onClick={() => setShowSettings(true)} className="bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 font-semibold px-4 py-2 rounded-xl transition-colors text-sm">⚙ Settings</button>
                  <button onClick={() => setShowResourcesModal(true)} className="bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 font-semibold px-4 py-2 rounded-xl transition-colors text-sm">Players</button>
                  <button onClick={() => setShowRolesModal(true)} className="bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 font-semibold px-4 py-2 rounded-xl transition-colors text-sm">Positions</button>
                  <button onClick={() => setView('new')} className="bg-primary hover:bg-accent text-white font-semibold px-6 py-2 rounded-xl transition-colors">+ New Project</button>
                </div>
              </div>
              {projects.length === 0 ? (
                <div className="bg-white rounded-2xl shadow p-10 text-center">
                  <p className="text-gray-400 text-lg mb-4">No projects yet!</p>
                  <button onClick={() => setView('new')} className="bg-primary hover:bg-accent text-white font-semibold px-8 py-3 rounded-xl transition-colors">Create your first project →</button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {projects.map((project) => (
                    <div key={project.id} className="bg-white rounded-2xl shadow p-6 flex justify-between items-center">
                      <div>
                        <h3 className="text-lg font-bold text-gray-800">{project.name}</h3>
                        <p className="text-sm text-gray-400 mt-1">{new Date(project.created_at).toLocaleDateString()}</p>
                        <div className="flex items-center gap-4 mt-2">
                          {project.project_rag && (
                            <span className="flex items-center gap-1.5 text-xs text-gray-500">
                              <span className={`w-3 h-3 rounded-full inline-block ${project.project_rag === 'Red' ? 'bg-red-500' : project.project_rag === 'Yellow' ? 'bg-yellow-400' : 'bg-green-500'}`} />
                              {project.project_rag}
                            </span>
                          )}
                          {project.project_state && (
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${project.project_state === 'Complete' ? 'bg-green-100 text-green-700' : project.project_state === 'In Progress' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                              {project.project_state}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <button onClick={() => openProject(project)} className="bg-primary hover:bg-accent text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors">Open</button>
                        <button onClick={() => copyProject(project)} className="bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-semibold px-4 py-2 rounded-xl transition-colors">Copy</button>
<button onClick={() => deleteProject(project.id)} className="bg-red-100 hover:bg-red-200 text-red-600 text-sm font-semibold px-4 py-2 rounded-xl transition-colors">Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Billing Success */}
          {view === 'billing-success' && (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="bg-white rounded-2xl shadow-xl p-12 max-w-md w-full text-center">
                <div className="text-5xl mb-6">🎉</div>
                <h2 className="text-2xl font-extrabold mb-3" style={{ color: '#3C3489' }}>
                  Welcome to gAImplans {userTier === 'unlimited' ? 'Unlimited' : 'Pro'}!
                </h2>
                <p className="text-gray-500 mb-8">Your subscription is now active. You're all set to unlock the full power of AI project planning.</p>
                <button
                  onClick={() => { setView('dashboard'); window.history.replaceState(null, '', '/'); }}
                  className="w-full font-bold py-3 rounded-xl text-sm transition-colors text-white"
                  style={{ backgroundColor: '#3C3489' }}
                >
                  Go to My Projects →
                </button>
              </div>
            </div>
          )}

          {/* Welcome / first-login onboarding */}
          {view === 'welcome' && (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="bg-white rounded-2xl shadow-xl p-10 md:p-14 max-w-2xl w-full text-center">
                <div className="text-5xl mb-4">👋</div>
                <h2 className="text-3xl font-extrabold mb-3" style={{ color: '#3C3489' }}>Welcome to gAImplans!</h2>
                <p className="text-gray-500 mb-10 text-base">We're excited to help you plan smarter. Here's how to get the most out of your account.</p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 text-left">
                  {[
                    { step: '1', title: 'Set up your Positions and Players', desc: 'Add job titles and hourly rates under Positions, and the people on your team under Players, so cost planning and staffing are ready to go.' },
                    { step: '2', title: 'Generate your first AI project plan', desc: 'Describe your project in plain English and let Claude AI build a full phase-by-phase plan with tasks, durations, and dependencies.' },
                    { step: '3', title: 'Build your team and start tracking', desc: 'Assign Players to tasks, set dates, and watch RAG status and progress update automatically as work gets done.' },
                  ].map(s => (
                    <div key={s.step} className="rounded-2xl p-5" style={{ backgroundColor: '#EEEDFE' }}>
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-extrabold text-white mb-3" style={{ backgroundColor: '#3C3489' }}>{s.step}</div>
                      <h4 className="font-bold text-sm mb-1.5" style={{ color: '#3C3489' }}>{s.title}</h4>
                      <p className="text-gray-500 text-xs leading-relaxed">{s.desc}</p>
                    </div>
                  ))}
                </div>

                <a href="/user-guide.html" target="_blank" rel="noopener noreferrer" className="text-sm font-semibold hover:opacity-80 transition-opacity" style={{ color: '#3C3489' }}>
                  📖 Read the full User Guide →
                </a>

                <div className="mt-8">
                  <button
                    onClick={() => { setView('dashboard'); window.history.replaceState(null, '', '/'); }}
                    className="font-bold px-10 py-4 rounded-xl text-base transition-colors text-white hover:opacity-90"
                    style={{ backgroundColor: '#3C3489' }}
                  >
                    Get Started → Create Your First Project
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* New Project */}
          {view === 'new' && (
            <div className="bg-white rounded-2xl shadow p-8">
              <div className="flex items-center gap-3 mb-6">
                <button onClick={() => setView('dashboard')} className="text-primary hover:text-accent font-semibold">← Back</button>
                <h2 className="text-2xl font-bold text-primary">New Project</h2>
              </div>
              <p className="text-gray-500 mb-6">Describe your project and our AI will generate a full plan for you.</p>
              <textarea
                className="w-full border border-gray-200 rounded-xl p-4 text-sm h-36 resize-none focus:outline-none focus:ring-2 focus:ring-accent"
                placeholder="e.g. Build a mobile app for tracking fitness goals with a 3 month timeline and a team of 4 people..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <button onClick={generatePlan} disabled={loading} className="mt-4 bg-primary hover:bg-accent text-white font-semibold px-8 py-3 rounded-xl transition-colors disabled:opacity-50">
                {loading ? 'Generating your plan...' : 'Generate Plan ⚡'}
              </button>
            </div>
          )}

          {/* Plan Preview */}
          {view === 'plan' && plan && (
            <div className="bg-white rounded-2xl shadow p-8">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <button onClick={() => setView(planOrigin)} className="text-primary hover:text-accent font-semibold">← Back</button>
                  <h2 className="text-2xl font-bold text-primary">{plan.projectName}</h2>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-semibold text-gray-600 whitespace-nowrap">Start Date</label>
                    <input
                      type="date"
                      value={planStartDate}
                      onChange={(e) => setPlanStartDate(e.target.value)}
                      className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                    />
                  </div>
                  <button onClick={savePlan} disabled={saving} className="bg-primary hover:bg-accent text-white font-semibold px-6 py-2 rounded-xl transition-colors disabled:opacity-50">
                    {saving ? 'Saving...' : '💾 Save Project'}
                  </button>
                </div>
              </div>
              {(() => {
                let taskCounter = 0;
                return plan.phases.map((phase: any, phaseIndex: number) => (
                  <div key={phaseIndex} className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-primary text-white text-sm font-bold px-3 py-1 rounded-full">Phase {phaseIndex + 1}</div>
                      <h3 className="text-lg font-semibold text-gray-800">{phase.name}</h3>
                    </div>
                    <div className="border border-gray-100 rounded-xl overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-warm">
                          <tr>
                            <th className="text-center px-4 py-3 text-primary font-semibold w-10">#</th>
                            <th className="text-left px-4 py-3 text-primary font-semibold">Task</th>
                            <th className="text-left px-4 py-3 text-primary font-semibold">Description</th>
                            <th className="text-center px-4 py-3 text-primary font-semibold">Days</th>
                            <th className="text-center px-4 py-3 text-primary font-semibold">Priority</th>
                            <th className="text-center px-4 py-3 text-primary font-semibold">Predecessors</th>
                          </tr>
                        </thead>
                        <tbody>
                          {phase.tasks.map((task: any, taskIndex: number) => {
                            taskCounter++;
                            const num = taskCounter;
                            return (
                              <tr key={taskIndex} className={`border-t border-gray-100 hover:bg-gray-200 transition-colors ${taskIndex % 2 === 0 ? 'bg-white' : 'bg-gray-100'}`}>
                                <td className="px-4 py-3 text-center text-gray-400 font-mono text-xs">{num}</td>
                                <td className="px-4 py-3 font-medium text-gray-800">{task.name}</td>
                                <td className="px-4 py-3 text-gray-500">{task.description}</td>
                                <td className="px-4 py-3 text-center text-gray-700">{task.estimatedDays}</td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${priorityColor(task.priority)}`}>{task.priority}</span>
                                </td>
                                <td className="px-4 py-3 text-center text-gray-500 text-xs">
                                  {task.predecessors && task.predecessors.length > 0 ? task.predecessors.join(', ') : <span className="text-gray-300">—</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}

          {/* Task Tracking */}
          {view === 'tasks' && currentProject && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  {editingProjectName ? (
                    <input
                      autoFocus
                      className="text-2xl font-bold text-primary border-b-2 border-accent focus:outline-none bg-transparent"
                      value={projectNameDraft}
                      onChange={e => setProjectNameDraft(e.target.value)}
                      onBlur={async () => {
                        setEditingProjectName(false);
                        const trimmed = projectNameDraft.trim();
                        if (trimmed && trimmed !== currentProject.name) {
                          try {
                            await apiFetch(`${API_URL}/api/projects/${currentProject.id}`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ name: trimmed })
                            });
                            setCurrentProject({ ...currentProject, name: trimmed });
                          } catch (err: any) { toast.error(errMsg(err, 'Failed to rename project')); }
                          loadProjects();
                        }
                      }}
                      onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingProjectName(false); }}
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <h2 className="text-2xl font-bold text-primary">{currentProject.name}</h2>
                      <button onClick={() => { setProjectNameDraft(currentProject.name); setEditingProjectName(true); }} className="text-gray-300 hover:text-accent transition-colors" title="Rename project">
                        ✏️
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex gap-3 flex-wrap">
<button onClick={() => { setChatOpen(v => !v); if (!chatOpen) setChatMessages([]); }} className="bg-primary hover:bg-accent text-white font-semibold px-5 py-2 rounded-xl transition-colors flex items-center gap-2">
                    💬 AI Coach
                  </button>


                  <div className="relative">
                    <button
                      onClick={() => setShowExportMenu(v => !v)}
                      className="bg-white border border-primary text-primary hover:bg-primary hover:text-white font-semibold px-5 py-2 rounded-xl transition-colors flex items-center gap-2">
                      Export ▾
                    </button>
                    {showExportMenu && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowExportMenu(false)} />
                        <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden">
                          <a
                            href={`${API_URL}/api/projects/${currentProject.id}/export/docx`}
                            onClick={() => setShowExportMenu(false)}
                            className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 font-medium">
                            📝 Word
                          </a>
                          <a
                            href={`${API_URL}/api/projects/${currentProject.id}/export/xlsx`}
                            onClick={() => setShowExportMenu(false)}
                            className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 font-medium border-t border-gray-100">
                            📊 Excel
                          </a>
                          <a
                            href={`${API_URL}/api/projects/${currentProject.id}/export/pdf`}
                            onClick={() => setShowExportMenu(false)}
                            className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 font-medium border-t border-gray-100">
                            📄 PDF
                          </a>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="border border-gray-300 rounded-xl overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-warm">
                    <tr>
                      <th className="px-2 py-3 border-b border-r border-gray-300 w-6"></th>
                      <th className="text-center px-2 py-3 text-primary font-semibold border-b border-r border-gray-300 w-8">#</th>
                      <th className="text-left px-4 py-3 text-primary font-semibold border-b border-r border-gray-300 min-w-64">Task</th>
                      <th className="text-left px-4 py-3 text-primary font-semibold border-b border-r border-gray-300">Player</th>
                      <th className="text-center px-4 py-3 text-primary font-semibold border-b border-r border-gray-300 min-w-32">Start</th>
                      <th className="text-center px-4 py-3 text-primary font-semibold border-b border-r border-gray-300 min-w-32">End</th>
                      <th className="text-center px-4 py-3 text-primary font-semibold border-b border-r border-gray-300">Duration (days)</th>
                      <th className="text-center px-4 py-3 text-primary font-semibold border-b border-r border-gray-300">Priority</th>
                      <th className="text-center px-4 py-3 text-primary font-semibold border-b border-r border-gray-300">%</th>
                      <th className="text-center px-4 py-3 text-primary font-semibold border-b border-r border-gray-300 min-w-36">State</th>
                      <th className="text-center px-4 py-3 text-primary font-semibold border-b border-r border-gray-300">Status</th>
                      <th className="text-center px-4 py-3 text-primary font-semibold border-b border-r border-gray-300 min-w-24">Predecessors</th>
                      <th className="text-left px-4 py-3 text-primary font-semibold border-b border-r border-gray-300 min-w-80">Progress Notes</th>
                      <th className="text-center px-4 py-3 text-primary font-semibold border-b border-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {phases.map((phase) => (
                      <Fragment key={phase}>
                        {/* Phase header row */}
                        <tr>
                          <td colSpan={14} className="px-4 py-2 text-white" style={{backgroundColor: '#3C3489'}}>
                            <div className="flex items-center gap-4">
                              {editingPhase === phase ? (
                                <input
                                  autoFocus
                                  value={phaseDraft}
                                  onChange={e => setPhaseDraft(e.target.value)}
                                  className="bg-transparent border-b border-purple-300 text-white font-bold text-sm focus:outline-none min-w-40"
                                  onBlur={() => { if (phaseDraft.trim() && phaseDraft !== phase) updatePhaseName(phase, phaseDraft.trim()); setEditingPhase(null); }}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') { if (phaseDraft.trim() && phaseDraft !== phase) updatePhaseName(phase, phaseDraft.trim()); setEditingPhase(null); }
                                    if (e.key === 'Escape') setEditingPhase(null);
                                  }}
                                />
                              ) : (
                                <span
                                  className="font-bold text-sm cursor-pointer"
                                  onDoubleClick={() => { setEditingPhase(phase); setPhaseDraft(phase); }}
                                  title="Double-click to rename">
                                  {phase}
                                </span>
                              )}
                              <button onClick={() => insertPhase(phase, 'above')} className="text-xs text-purple-200 hover:text-white font-semibold">↑ Insert Above</button>
                              <button onClick={() => insertPhase(phase, 'below')} className="text-xs text-purple-200 hover:text-white font-semibold">↓ Insert Below</button>
                              <button onClick={() => deletePhase(phase)} className="text-xs text-red-300 hover:text-white font-semibold">Delete</button>
                            </div>
                          </td>
                        </tr>
                        {tasks.filter(t => t.phase_name === phase).map((task, taskIndex) => (
                          <tr
                            key={task.id}
                            data-task-id={task.id}
                            draggable={inlineEdit?.taskId !== task.id}
                            onDragStart={() => { dragTaskId.current = task.id; }}
                            onDragOver={(e) => { e.preventDefault(); setDragOverTaskId(task.id); }}
                            onDragLeave={() => setDragOverTaskId(null)}
                            onDrop={() => {
                              setDragOverTaskId(null);
                              const fromId = dragTaskId.current;
                              if (!fromId || fromId === task.id) return;
                              const phaseTasks = tasks.filter(t => t.phase_name === phase);
                              const fromIdx = phaseTasks.findIndex(t => t.id === fromId);
                              const toIdx = phaseTasks.findIndex(t => t.id === task.id);
                              const reordered = [...phaseTasks];
                              const [moved] = reordered.splice(fromIdx, 1);
                              const adjustedToIdx = fromIdx < toIdx ? toIdx - 1 : toIdx;
                              reordered.splice(adjustedToIdx, 0, moved);
                              reorderTasks(phase, reordered.map(t => t.id));
                              dragTaskId.current = null;
                            }}
                            onDragEnd={() => { dragTaskId.current = null; setDragOverTaskId(null); setDragOverPhaseEnd(null); }}
                            className={`border-t border-gray-100 transition-colors ${dragOverTaskId === task.id ? 'border-t-2 border-t-primary bg-blue-50' : taskIndex % 2 === 0 ? 'bg-white hover:bg-gray-200' : 'bg-gray-100 hover:bg-gray-200'}`}
                          >
                            {/* Grip handle */}
                            <td className="px-2 py-3 border-b border-r border-gray-200 text-gray-300 cursor-grab active:cursor-grabbing text-center select-none">⠿</td>
                            {/* Task Number */}
                            <td className="px-2 py-3 border-b border-r border-gray-200 text-center text-xs text-gray-400 select-none">{taskNumbers.get(task.id)}</td>
                            {/* Task Name */}
                            <td className="px-4 py-3 font-medium text-gray-800 border-b border-r border-gray-200 cursor-pointer" onDoubleClick={() => setInlineEdit({ taskId: task.id, field: 'name' })}>
                              {inlineEdit?.taskId === task.id && inlineEdit.field === 'name' ? (
                                <input
                                  autoFocus
                                  defaultValue={task.name}
                                  className="w-full border border-primary rounded px-2 py-1 text-sm focus:outline-none"
                                  onBlur={(e) => updateTaskField(task, 'name', e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') updateTaskField(task, 'name', (e.target as HTMLInputElement).value);
                                    if (e.key === 'Escape') setInlineEdit(null);
                                  }}
                                />
                              ) : task.name}
                            </td>
                            {/* Assignee */}
                            <td className="px-4 py-3 text-gray-500 border-b border-r border-gray-200 cursor-pointer" onDoubleClick={() => setInlineEdit({ taskId: task.id, field: 'assignee_id' })}>
                              {inlineEdit?.taskId === task.id && inlineEdit.field === 'assignee_id' ? (
                                <select
                                  autoFocus
                                  defaultValue={task.assignee_id ?? ''}
                                  className="w-full border border-primary rounded px-2 py-1 text-sm focus:outline-none"
                                  onChange={(e) => updateTaskField(task, 'assignee_id', e.target.value || null)}
                                  onBlur={() => setInlineEdit(null)}
                                >
                                  <option value="">Unassigned</option>
                                  {team.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                                </select>
                              ) : (task.assignee_name || <span className="text-gray-300">Unassigned</span>)}
                            </td>
                            {/* Start Date */}
                            <td className="px-4 py-3 text-center text-gray-500 border-b border-r border-gray-200 cursor-pointer" onDoubleClick={() => setInlineEdit({ taskId: task.id, field: 'start_date' })}>
                              {inlineEdit?.taskId === task.id && inlineEdit.field === 'start_date' ? (
                                <input
                                  autoFocus
                                  type="date"
                                  defaultValue={task.start_date ? new Date(task.start_date).toISOString().split('T')[0] : ''}
                                  max={task.end_date ? task.end_date.substring(0, 10) : undefined}
                                  className="border border-primary rounded px-2 py-1 text-sm focus:outline-none"
                                  onBlur={(e) => updateTaskField(task, 'start_date', e.target.value || null)}
                                  onKeyDown={(e) => { if (e.key === 'Escape') setInlineEdit(null); }}
                                />
                              ) : (task.start_date ? new Date(task.start_date).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '—')}
                            </td>
                            {/* End Date */}
                            <td className="px-4 py-3 text-center text-gray-500 border-b border-r border-gray-200 cursor-pointer" onDoubleClick={() => setInlineEdit({ taskId: task.id, field: 'end_date' })}>
                              {inlineEdit?.taskId === task.id && inlineEdit.field === 'end_date' ? (
                                <input
                                  autoFocus
                                  type="date"
                                  defaultValue={task.end_date ? new Date(task.end_date).toISOString().split('T')[0] : ''}
                                  min={task.start_date ? task.start_date.substring(0, 10) : undefined}
                                  className="border border-primary rounded px-2 py-1 text-sm focus:outline-none"
                                  onBlur={(e) => updateTaskField(task, 'end_date', e.target.value || null)}
                                  onKeyDown={(e) => { if (e.key === 'Escape') setInlineEdit(null); }}
                                />
                              ) : (task.end_date ? new Date(task.end_date).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '—')}
                            </td>
                            {/* Duration */}
                            <td className="px-4 py-3 text-center text-gray-500 border-b border-r border-gray-200">
                              {(() => {
                                const start = task.start_date ? new Date(task.start_date) : null;
                                const end = task.end_date ? new Date(task.end_date) : null;
                                if (start && end && end >= start) {
                                  if (settings.include_weekends) {
                                    const days = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                                    return `${days}d`;
                                  } else {
                                    let days = 0;
                                    const cursor = new Date(start);
                                    while (cursor <= end) {
                                      if (cursor.getDay() !== 0 && cursor.getDay() !== 6) days++;
                                      cursor.setDate(cursor.getDate() + 1);
                                    }
                                    return `${days}d`;
                                  }
                                }
                                return task.estimated_days ? `${task.estimated_days}d` : '—';
                              })()}
                            </td>
                            {/* Priority */}
                            <td className="px-4 py-3 text-center border-b border-r border-gray-200 cursor-pointer" onDoubleClick={() => setInlineEdit({ taskId: task.id, field: 'priority' })}>
                              {inlineEdit?.taskId === task.id && inlineEdit.field === 'priority' ? (
                                <select
                                  autoFocus
                                  defaultValue={task.priority}
                                  className="border border-primary rounded px-2 py-1 text-sm focus:outline-none"
                                  onChange={(e) => updateTaskField(task, 'priority', e.target.value)}
                                  onBlur={() => setInlineEdit(null)}
                                >
                                  <option>High</option>
                                  <option>Medium</option>
                                  <option>Low</option>
                                </select>
                              ) : <span className={`px-2 py-1 rounded-full text-xs font-semibold ${priorityColor(task.priority)}`}>{task.priority}</span>}
                            </td>
                            {/* % Complete */}
                            <td className="px-4 py-3 text-center border-b border-r border-gray-200 cursor-pointer" onDoubleClick={() => setInlineEdit({ taskId: task.id, field: 'percent_complete' })}>
                              {inlineEdit?.taskId === task.id && inlineEdit.field === 'percent_complete' ? (
                                <input
                                  autoFocus
                                  type="number"
                                  min="1"
                                  max="100"
                                  defaultValue={task.percent_complete ?? ''}
                                  className="w-16 border border-primary rounded px-2 py-1 text-sm text-center focus:outline-none"
                                  onBlur={(e) => {
                                    const val = Math.min(100, Math.max(1, Number(e.target.value)));
                                    updateTaskField(task, 'percent_complete', e.target.value ? val : null);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      const val = Math.min(100, Math.max(1, Number((e.target as HTMLInputElement).value)));
                                      updateTaskField(task, 'percent_complete', (e.target as HTMLInputElement).value ? val : null);
                                    }
                                    if (e.key === 'Escape') setInlineEdit(null);
                                  }}
                                />
                              ) : (
                                <span className="text-gray-600 text-sm">{task.percent_complete != null ? `${task.percent_complete}%` : '—'}</span>
                              )}
                            </td>
                            {/* Status */}
                            <td className="px-4 py-3 text-center border-b border-r border-gray-200 cursor-pointer" onDoubleClick={() => setInlineEdit({ taskId: task.id, field: 'status' })}>
                              {inlineEdit?.taskId === task.id && inlineEdit.field === 'status' ? (
                                <select
                                  autoFocus
                                  defaultValue={task.status}
                                  className="border border-primary rounded px-2 py-1 text-sm focus:outline-none"
                                  onChange={(e) => updateTaskField(task, 'status', e.target.value)}
                                  onBlur={() => setInlineEdit(null)}
                                >
                                  <option>Not Started</option>
                                  <option>In Progress</option>
                                  <option>Complete</option>
                                  <option>Blocked</option>
                                </select>
                              ) : <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColor(task.status)}`}>{task.status}</span>}
                            </td>
                            {/* Status (RAG) */}
                            <td className="px-4 py-3 text-center border-b border-r border-gray-200 cursor-pointer" onDoubleClick={() => setInlineEdit({ taskId: task.id, field: 'rag_status' })}>
                              {inlineEdit?.taskId === task.id && inlineEdit.field === 'rag_status' ? (
                                <select
                                  autoFocus
                                  defaultValue={task.rag_status ?? ''}
                                  className="border border-primary rounded px-2 py-1 text-sm focus:outline-none"
                                  onChange={(e) => updateTaskField(task, 'rag_status', e.target.value || null)}
                                  onBlur={() => setInlineEdit(null)}
                                >
                                  <option value="">—</option>
                                  <option value="Red">Red</option>
                                  <option value="Yellow">Yellow</option>
                                  <option value="Green">Green</option>
                                </select>
                              ) : (
                                <span className={`inline-block w-4 h-4 rounded-full ${task.rag_status === 'Red' ? 'bg-red-500' : task.rag_status === 'Yellow' ? 'bg-yellow-400' : task.rag_status === 'Green' ? 'bg-green-500' : 'bg-gray-200'}`} title={task.rag_status ?? 'Not set'} />
                              )}
                            </td>
                            {/* Progress Notes */}
                            {/* Predecessors */}
                            <td
                              className="px-4 py-3 border-b border-r border-gray-200 text-center cursor-pointer"
                              onDoubleClick={() => { setDepsModalTaskId(task.id); setDepsModalSelected([...task.depends_on_ids]); }}
                              title="Double-click to edit predecessors"
                            >
                              <span className="text-xs text-gray-600">
                                {task.depends_on_ids.length > 0
                                  ? task.depends_on_ids.map(id => taskNumbers.get(id)).filter(Boolean).sort((a, b) => (a as number) - (b as number)).join(', ')
                                  : <span className="text-gray-300">—</span>}
                              </span>
                            </td>
                            <td
                              className="px-4 py-3 border-b border-r border-gray-200 min-w-80 cursor-pointer align-top"
                              onDoubleClick={() => {
                                const timestamp = new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
                                const existing = task.progress_notes ? `\n${task.progress_notes}` : '';
                                setInlineEdit({ taskId: task.id, field: 'progress_notes', initialValue: `${timestamp}: ${existing}` });
                              }}
                            >
                              {inlineEdit?.taskId === task.id && inlineEdit.field === 'progress_notes' ? (
                                <textarea
                                  ref={(el) => {
                                    if (el) {
                                      const pos = (inlineEdit?.initialValue ?? '').indexOf(': ') + 2;
                                      el.focus();
                                      el.setSelectionRange(pos, pos);
                                    }
                                  }}
                                  defaultValue={inlineEdit.initialValue}
                                  rows={4}
                                  className="w-full border border-primary rounded px-2 py-1 text-xs focus:outline-none resize-none"
                                  onBlur={(e) => updateTaskField(task, 'progress_notes', e.target.value || null)}
                                  onKeyDown={(e) => { if (e.key === 'Escape') setInlineEdit(null); }}
                                />
                              ) : (
                                <div className="text-xs text-gray-600 whitespace-pre-wrap">{task.progress_notes || <span className="text-gray-300">Double-click to add notes</span>}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center border-b border-gray-200">
                              <div className="flex gap-2 justify-center">
                                <button onClick={() => deleteTask(task.id)} className="text-red-400 hover:text-red-600 font-semibold text-xs">Delete</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {/* Add task row — also serves as drop target for moving a task to last position */}
                        <tr
                          onDragOver={(e) => { e.preventDefault(); setDragOverPhaseEnd(phase); setDragOverTaskId(null); }}
                          onDragLeave={() => setDragOverPhaseEnd(null)}
                          onDrop={() => {
                            setDragOverPhaseEnd(null);
                            const fromId = dragTaskId.current;
                            if (!fromId) return;
                            const phaseTasks = tasks.filter(t => t.phase_name === phase);
                            const fromIdx = phaseTasks.findIndex(t => t.id === fromId);
                            if (fromIdx === -1) return;
                            const reordered = [...phaseTasks];
                            const [moved] = reordered.splice(fromIdx, 1);
                            reordered.push(moved);
                            reorderTasks(phase, reordered.map(t => t.id));
                            dragTaskId.current = null;
                          }}
                          className={dragOverPhaseEnd === phase ? 'border-t-2 border-t-primary bg-blue-50' : ''}
                        >
                          <td colSpan={14} className="px-4 py-2 bg-gray-50 border-t border-gray-200">
                            <button onClick={() => addTask(phase)} className="text-sm text-primary hover:text-accent font-semibold">
                              + Add Task to {phase}
                            </button>
                          </td>
                        </tr>
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Team Management */}
          {view === 'team' && currentProject && (
            <div>
              <div className="flex justify-between items-end mb-6">
                <h2 className="text-2xl font-bold text-primary">Team — {currentProject.name}</h2>
                <div className="flex flex-col items-end gap-1">
                  <a href={`${API_URL}/api/projects/${currentProject.id}/team/export/xlsx`} className="bg-white border border-primary text-primary hover:bg-primary hover:text-white font-semibold px-4 py-1.5 rounded-xl transition-colors text-sm">
                    📊 Export Excel
                  </a>
                  <button onClick={() => setAddingMember(true)} className="bg-primary hover:bg-accent text-white font-semibold px-5 py-2 rounded-xl transition-colors">+ Add Member</button>
                </div>
              </div>

              {addingMember && (
                <div className="bg-white rounded-2xl shadow p-6 mb-6">
                  <h3 className="font-bold text-primary mb-4">Add Team Member</h3>
                  {resources.length === 0 ? (
                    <p className="text-sm text-gray-400 mb-4">No players defined yet. Add players from the <strong>Players</strong> button on the My Projects dashboard first.</p>
                  ) : (
                    <div className="grid grid-cols-4 gap-4 mb-4">
                      <div>
                        <label className="text-xs text-gray-500 font-semibold mb-1 block">Player *</label>
                        <select
                          className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                          value={newMember.name}
                          onChange={e => {
                            const r = resources.find(r => r.name === e.target.value);
                            setNewMember(r ? { name: r.name, email: r.email || '', role: r.role || '', allocated_hours: '' } : { name: '', email: '', role: '', allocated_hours: '' });
                          }}>
                          <option value="">Select a player…</option>
                          {resources.filter(r => !team.some(m => m.name === r.name)).map(r => (
                            <option key={r.id} value={r.name}>{r.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 font-semibold mb-1 block">Email</label>
                        <div className="px-4 py-2 text-sm text-gray-500 bg-gray-50 rounded-xl border border-gray-100 min-h-[38px]">{newMember.email || <span className="text-gray-300">—</span>}</div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 font-semibold mb-1 block">Position</label>
                        <div className="px-4 py-2 text-sm text-gray-500 bg-gray-50 rounded-xl border border-gray-100 min-h-[38px]">{newMember.role || <span className="text-gray-300">—</span>}</div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 font-semibold mb-1 block">Allocated Hours</label>
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          placeholder="0"
                          className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                          value={newMember.allocated_hours}
                          onChange={e => setNewMember(prev => ({ ...prev, allocated_hours: e.target.value }))}
                        />
                      </div>
                    </div>
                  )}
                  <div className="flex gap-3">
                    <button onClick={addTeamMember} disabled={!newMember.name} className="bg-primary hover:bg-accent text-white font-semibold px-5 py-2 rounded-xl transition-colors disabled:opacity-40">Add</button>
                    <button onClick={() => { setAddingMember(false); setNewMember({ name: '', email: '', role: '', allocated_hours: '' }); }} className="bg-gray-100 hover:bg-gray-200 text-gray-600 font-semibold px-5 py-2 rounded-xl transition-colors">Cancel</button>
                  </div>
                </div>
              )}

              {team.length === 0 ? (
                <div className="bg-white rounded-2xl shadow p-10 text-center">
                  <p className="text-gray-400 mb-4">No team members yet.</p>
                  <button onClick={() => setAddingMember(true)} className="bg-primary hover:bg-accent text-white font-semibold px-6 py-2 rounded-xl transition-colors">Add your first team member →</button>
                </div>
              ) : (() => {
                const today = new Date();
                const todaySunday = new Date(today);
                todaySunday.setUTCDate(today.getUTCDate() - today.getUTCDay());
                const taskDates = tasks.filter(t => t.start_date).map(t => new Date(String(t.start_date).substring(0, 10) + 'T12:00:00Z'));
                const taskEndDates = tasks.filter(t => t.end_date).map(t => new Date(String(t.end_date).substring(0, 10) + 'T12:00:00Z'));
                let firstSunday: Date;
                if (taskDates.length > 0) {
                  const minDate = new Date(Math.min(...taskDates.map(d => d.getTime())));
                  const projSunday = new Date(minDate);
                  projSunday.setUTCDate(minDate.getUTCDate() - minDate.getUTCDay());
                  firstSunday = projSunday.getTime() < todaySunday.getTime() ? new Date(projSunday) : new Date(todaySunday);
                } else {
                  firstSunday = new Date(todaySunday);
                }
                const maxTaskEnd = taskEndDates.length > 0 ? new Date(Math.max(...taskEndDates.map(d => d.getTime()))) : new Date(today);
                const lastMoment = new Date(Math.max(maxTaskEnd.getTime(), todaySunday.getTime()));
                lastMoment.setUTCDate(lastMoment.getUTCDate() + 14);
                const numWeeks = Math.ceil((lastMoment.getTime() - firstSunday.getTime()) / (7 * 24 * 3600 * 1000)) + 1;
                const weekCols: Date[] = [];
                for (let i = 0; i < numWeeks; i++) {
                  const w = new Date(firstSunday);
                  w.setUTCDate(firstSunday.getUTCDate() + i * 7);
                  weekCols.push(w);
                }
                const isoWeek = (d: Date) => d.toISOString().split('T')[0];
                const todayWeekStr = isoWeek(todaySunday);
                const fmtWeek = (d: Date) => `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
                const getActual = (memberId: number, weekStart: string) =>
                  actuals.find(a => a.team_member_id === memberId && String(a.week_start).substring(0, 10) === weekStart);
                const ROW_H = 36;
                const CELL_CLS = 'border-r border-b border-gray-200';
                const TH_STYLE: React.CSSProperties = {position: 'sticky', top: 0, zIndex: 2};
                return (
                  <div className="bg-white rounded-2xl shadow flex" style={{overflow: 'clip'}}>
                    {/* Fixed columns */}
                    <div className="flex-shrink-0" style={{borderRight: '2px solid #D1D5DB', boxShadow: '2px 0 4px rgba(0,0,0,0.08)'}}>
                      <table className="text-xs" style={{borderCollapse: 'collapse', tableLayout: 'fixed', width: 526}}>
                        <thead>
                          <tr style={{height: ROW_H}}>
                            <th style={{...TH_STYLE, width: 36}} className={`px-2 bg-warm ${CELL_CLS}`}></th>
                            <th style={{...TH_STYLE, width: 150}} className={`text-left px-2 text-primary font-semibold bg-warm ${CELL_CLS}`}>Name</th>
                            <th style={{...TH_STYLE, width: 150}} className={`text-left px-2 text-primary font-semibold bg-warm ${CELL_CLS}`}>Email</th>
                            <th style={{...TH_STYLE, width: 110}} className={`text-left px-2 text-primary font-semibold bg-warm ${CELL_CLS}`}>Position</th>
                            <th style={{...TH_STYLE, width: 80}} className={`text-right px-2 text-primary font-semibold bg-warm border-b border-gray-200`}>Allocated</th>
                          </tr>
                        </thead>
                        <tbody>
                          {team.map((member) => {
                            const hasActuals = actuals.some(a => a.team_member_id === member.id && Number(a.hours) > 0);
                            const isHovered = hoveredMemberId === member.id;
                            return (
                            <tr key={member.id} style={{height: ROW_H}}
                              className={`group transition-colors ${isHovered ? 'bg-warm' : ''}`}
                              onMouseEnter={() => setHoveredMemberId(member.id)}
                              onMouseLeave={() => setHoveredMemberId(null)}>
                              <td className={`px-2 text-center ${CELL_CLS}`}>
                                <button
                                  onClick={() => !hasActuals && deleteTeamMember(member.id)}
                                  disabled={hasActuals}
                                  className={hasActuals ? 'text-gray-200 cursor-not-allowed' : 'text-red-400 hover:text-red-600'}
                                  title={hasActuals ? 'Cannot remove — this member has recorded actuals' : 'Remove'}>
                                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
                                </button>
                              </td>
                              <td className={`px-2 ${CELL_CLS}`}>
                                <select
                                  className={`w-full border border-gray-200 rounded-lg px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-accent ${hasActuals ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : isHovered ? 'bg-warm' : 'bg-white'}`}
                                  value={member.name || ''}
                                  disabled={hasActuals}
                                  onChange={e => assignTeamResource(member, e.target.value)}>
                                  <option value="">— Unassigned —</option>
                                  {resources.map(r => (
                                    <option key={r.id} value={r.name}>{r.name}</option>
                                  ))}
                                </select>
                              </td>
                              <td className={`px-2 text-gray-700 truncate ${CELL_CLS}`}>{member.email || <span className="text-gray-300">—</span>}</td>
                              <td className={`px-2 text-gray-700 truncate ${CELL_CLS}`}>{member.role}</td>
                              <td className="px-2 text-right border-b border-gray-200">
                                {editingMemberId === member.id ? (
                                  <input autoFocus type="number" min="0" step="0.5"
                                    className="w-16 border border-accent rounded px-1 py-0.5 text-xs text-right focus:outline-none"
                                    value={memberHoursDraft}
                                    onChange={e => setMemberHoursDraft(e.target.value)}
                                    onBlur={() => saveMemberHours(member, memberHoursDraft)}
                                    onKeyDown={e => { if (e.key === 'Enter') saveMemberHours(member, memberHoursDraft); if (e.key === 'Escape') setEditingMemberId(null); }}
                                  />
                                ) : (
                                  <span className="cursor-pointer text-gray-700 hover:text-accent" title="Click to edit"
                                    onClick={() => { setEditingMemberId(member.id); setMemberHoursDraft(member.allocated_hours != null ? String(member.allocated_hours) : ''); }}>
                                    {member.allocated_hours != null ? member.allocated_hours : <span className="text-gray-300">—</span>}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );})}
                        </tbody>
                      </table>
                    </div>
                    {/* Scrollable actuals — header and body are separate so the header
                        can be position:sticky relative to the page without being blocked
                        by the overflow:auto scroll container needed for horizontal scroll */}
                    <div className="flex-1 flex flex-col" style={{minWidth: 0}}>
                      {/* Sticky header: position:sticky on this div; overflow:hidden lets
                          us programmatically sync scrollLeft without creating a scroll
                          container that would break sticky on a child th */}
                      <div ref={teamHeaderScrollRef} style={{position: 'sticky', top: 0, zIndex: 2, overflowX: 'hidden'}}>
                        <table className="text-xs" style={{borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: `${weekCols.length * 68}px`}}>
                          <thead>
                            <tr style={{height: ROW_H}}>
                              {weekCols.map((w, i) => {
                                const isCurrentWeek = isoWeek(w) === todayWeekStr;
                                return (
                                  <th key={i} style={{width: 68}}
                                    data-current-week={isCurrentWeek ? 'true' : undefined}
                                    className={`text-right px-2 font-semibold border-r border-b border-gray-200 ${isCurrentWeek ? 'bg-blue-50 text-blue-700' : 'bg-warm text-primary'}`}>
                                    {fmtWeek(w)}
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                        </table>
                      </div>
                      {/* Horizontally-scrollable body; onScroll keeps header in sync */}
                      <div ref={teamScrollRef} style={{overflowX: 'auto'}}
                           onScroll={e => { if (teamHeaderScrollRef.current) teamHeaderScrollRef.current.scrollLeft = e.currentTarget.scrollLeft; }}>
                        <table className="text-xs" style={{borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: `${weekCols.length * 68}px`}}>
                          <colgroup>{weekCols.map((_, i) => <col key={i} style={{width: 68}} />)}</colgroup>
                          <tbody>
                            {team.map((member) => {
                              const isHovered = hoveredMemberId === member.id;
                              return (
                              <tr key={member.id} style={{height: ROW_H}}
                                className={`transition-colors ${isHovered ? 'bg-warm' : ''}`}
                                onMouseEnter={() => setHoveredMemberId(member.id)}
                                onMouseLeave={() => setHoveredMemberId(null)}>
                                {weekCols.map((w, i) => {
                                  const ws = isoWeek(w);
                                  const key = `${member.id}_${ws}`;
                                  const actual = getActual(member.id, ws);
                                  const hrs = actual ? Number(actual.hours) : null;
                                  const isCurrentWeekCell = ws === todayWeekStr;
                                  return (
                                    <td key={i}
                                      className={`border-r border-b border-gray-200 text-right cursor-pointer${isCurrentWeekCell ? ' bg-blue-50' : ''}`}
                                      style={{padding: '0 8px'}}
                                      onClick={() => { if (editingActualKey !== key) { setEditingActualKey(key); setActualsDraft(hrs != null && hrs > 0 ? String(hrs) : ''); }}}>
                                      {editingActualKey === key ? (
                                        <input autoFocus type="number" min="0" step="0.5"
                                          className="w-14 border border-accent rounded px-1 py-0.5 text-xs text-right focus:outline-none"
                                          value={actualsDraft}
                                          onChange={e => setActualsDraft(e.target.value)}
                                          onBlur={() => saveActual(member.id, ws, actualsDraft)}
                                          onKeyDown={e => { if (e.key === 'Enter') saveActual(member.id, ws, actualsDraft); if (e.key === 'Escape') setEditingActualKey(null); }}
                                          onClick={e => e.stopPropagation()}
                                        />
                                      ) : (
                                        <span className={hrs != null && hrs > 0 ? 'text-gray-700' : 'text-gray-300'}>
                                          {hrs != null && hrs > 0 ? hrs : '+'}
                                        </span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            );})}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
          {/* Status Reports */}
          {view === 'reports' && currentProject && (
            <div className="max-w-5xl mx-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-primary">Status Reports — {currentProject.name}</h2>
                <button onClick={generateReport} disabled={generatingReport} className="bg-primary hover:bg-accent text-white font-semibold px-5 py-2 rounded-xl transition-colors disabled:opacity-50">
                  {generatingReport ? 'Generating...' : '+ New Report'}
                </button>
              </div>
              {reports.length === 0 ? (
                <div className="bg-white rounded-2xl shadow p-10 text-center">
                  <p className="text-gray-400 mb-4">No reports yet.</p>
                  <button onClick={generateReport} disabled={generatingReport} className="bg-primary hover:bg-accent text-white font-semibold px-6 py-2 rounded-xl transition-colors disabled:opacity-50">
                    {generatingReport ? 'Generating...' : 'Generate your first report →'}
                  </button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {reports.map(report => (
                    <div key={report.id} className="bg-white rounded-2xl shadow p-6 flex justify-between items-start">
                      <div className="flex-1 min-w-0 pr-6">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-xs text-gray-400">{new Date(report.created_at).toLocaleString()}</p>
                          <span className={`w-2.5 h-2.5 rounded-full inline-block ${report.rag_status === 'Red' ? 'bg-red-500' : report.rag_status === 'Yellow' ? 'bg-yellow-400' : report.rag_status === 'Green' ? 'bg-green-500' : 'bg-gray-300'}`} title={report.rag_status ?? 'No status'} />
                        </div>
                        <p className="text-gray-700 text-sm">{report.summary}</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          onClick={() => setEditingReport({ ...report })}
                          className="bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-semibold px-4 py-2 rounded-xl transition-colors">
                          Edit
                        </button>
                        <a
                          href={`${API_URL}/api/reports/${report.id}/export/docx`}
                          download
                          className="bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-semibold px-4 py-2 rounded-xl transition-colors">
                          ⬇ Word
                        </a>
                        <button
                          onClick={() => confirm('Are you sure you want to delete this report?', async () => { try { await apiFetch(`${API_URL}/api/reports/${report.id}`, { method: 'DELETE' }); setReports(prev => prev.filter(r => r.id !== report.id)); toast.success('Report deleted'); } catch (err: any) { toast.error(errMsg(err, 'Failed to delete report')); } })}
                          className="bg-red-100 hover:bg-red-200 text-red-600 text-sm font-semibold px-4 py-2 rounded-xl transition-colors">
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Gantt Chart */}
          {view === 'gantt' && currentProject && (
            <div>
              <div className="flex justify-end mb-4">
                <div className="relative">
                  <button
                    onClick={() => setShowGanttExportMenu(v => !v)}
                    className="bg-white border border-primary text-primary hover:bg-primary hover:text-white font-semibold px-5 py-2 rounded-xl transition-colors">
                    Export ▾
                  </button>
                  {showGanttExportMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowGanttExportMenu(false)} />
                      <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden">
                        <button
                          onClick={exportGanttPng}
                          className="flex items-center gap-2 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 font-medium">
                          🖼 PNG
                        </button>
                        <button
                          onClick={exportGanttPdf}
                          className="flex items-center gap-2 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 font-medium border-t border-gray-100">
                          📄 PDF
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div ref={ganttRef}>
                <GanttChart tasks={tasks} onUpdateDates={updateTaskDates} scrollRef={ganttScrollRef} />
              </div>
            </div>
          )}

          {/* Cost Plan */}
          {view === 'costs' && currentProject && (() => {
            const laborTotal = laborItems.reduce((sum, i) => sum + (Number(i.hours) * Number(i.hourly_rate || 0)), 0);
            const nonLaborTotal = nonLaborItems.reduce((sum, i) => sum + (Number(i.units) * Number(i.unit_cost)), 0);
            const grandTotal = laborTotal + nonLaborTotal;
            const fmt = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
            return (
              <div className="p-6 max-w-5xl mx-auto space-y-8">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-primary">Cost Plan — {currentProject.name}</h2>
                  <div className="bg-primary rounded-2xl px-6 py-3 flex items-center gap-4 text-white">
                    <span className="text-sm font-semibold opacity-80">Total Project Cost</span>
                    <span className="text-2xl font-extrabold">{fmt(grandTotal)}</span>
                  </div>
                </div>

                {/* Labor */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-primary">Labor</h3>
                    <button
                      onClick={buildTeamFromCosts}
                      disabled={laborItems.length === 0 || laborItems.every(i => i.team_created)}
                      title={laborItems.every(i => i.team_created) ? 'All labor records have already been used to build team slots' : 'Create a team slot for each unused labor record'}
                      className="bg-accent hover:bg-primary text-white text-xs font-semibold px-4 py-2 rounded-xl transition-colors disabled:opacity-40">
                      + Build Team from Labor
                    </button>
                  </div>
                  <div className="bg-white rounded-2xl shadow overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-warm">
                        <tr>
                          <th className="text-left px-4 py-3 text-primary font-semibold border-b border-gray-200">Position</th>
                          <th className="text-right px-4 py-3 text-primary font-semibold border-b border-gray-200">Hours</th>
                          <th className="text-right px-4 py-3 text-primary font-semibold border-b border-gray-200">Rate</th>
                          <th className="text-right px-4 py-3 text-primary font-semibold border-b border-gray-200">Total</th>
                          <th className="px-4 py-3 border-b border-gray-200"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {laborItems.map(item => (
                          <tr key={item.id} className="border-t border-gray-100 hover:bg-gray-50">
                            {editingLaborId === item.id ? (
                              <>
                                <td className="px-4 py-2">
                                  <select autoFocus className="w-full border border-primary rounded px-2 py-1 text-sm focus:outline-none" value={laborDraft.roleId} onChange={e => setLaborDraft(d => ({...d, roleId: e.target.value}))}>
                                    <option value="">— Select position —</option>
                                    {roles.map(r => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
                                  </select>
                                </td>
                                <td className="px-4 py-2">
                                  <input type="number" min="0" step="0.5" className="w-full border border-gray-200 rounded px-2 py-1 text-sm text-right focus:outline-none" value={laborDraft.hours} onChange={e => setLaborDraft(d => ({...d, hours: e.target.value}))} />
                                </td>
                                <td className="px-4 py-2 text-right text-gray-400 text-xs">
                                  {roles.find(r => String(r.id) === laborDraft.roleId)?.hourly_rate != null ? fmt(Number(roles.find(r => String(r.id) === laborDraft.roleId)?.hourly_rate)) + '/hr' : '—'}
                                </td>
                                <td className="px-4 py-2 text-right text-gray-400 text-xs">—</td>
                                <td className="px-4 py-2 text-right whitespace-nowrap">
                                  <button onClick={saveLaborEdit} className="text-primary hover:text-accent font-semibold text-xs mr-3">Save</button>
                                  <button onClick={() => { setEditingLaborId(null); setLaborDraft({ roleId: '', hours: '' }); }} className="text-gray-400 hover:text-gray-600 text-xs">Cancel</button>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="px-4 py-3 font-medium text-gray-800">{item.role_name || <span className="text-gray-300">—</span>}</td>
                                <td className="px-4 py-3 text-right text-gray-600">{Number(item.hours).toLocaleString()}</td>
                                <td className="px-4 py-3 text-right text-gray-500">{item.hourly_rate != null ? fmt(Number(item.hourly_rate)) + '/hr' : <span className="text-gray-300">—</span>}</td>
                                <td className="px-4 py-3 text-right font-semibold text-gray-800">{fmt(Number(item.hours) * Number(item.hourly_rate || 0))}</td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                  {item.team_created && <span className="text-green-500 text-xs mr-3" title="Team slot created">✓ Team</span>}
                                  <button onClick={() => { setEditingLaborId(item.id); setLaborDraft({ roleId: String(item.role_id || ''), hours: String(item.hours) }); }} className="text-primary hover:text-accent font-semibold text-xs mr-3">Edit</button>
                                  <button onClick={() => deleteLaborItem(item.id)} className="text-red-400 hover:text-red-600 font-semibold text-xs">Delete</button>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                        <tr className="border-t border-gray-200 bg-gray-50">
                          <td className="px-4 py-2">
                            <select className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent" value={newLaborRoleId} onChange={e => setNewLaborRoleId(e.target.value)}>
                              <option value="">— Select position —</option>
                              {roles.map(r => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
                            </select>
                          </td>
                          <td className="px-4 py-2">
                            <input type="number" min="0" step="0.5" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-accent" placeholder="Hours" value={newLaborHours} onChange={e => setNewLaborHours(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addLaborItem(); }} />
                          </td>
                          <td className="px-4 py-2 text-right text-gray-400 text-xs">
                            {newLaborRoleId && roles.find(r => String(r.id) === newLaborRoleId)?.hourly_rate != null ? fmt(Number(roles.find(r => String(r.id) === newLaborRoleId)?.hourly_rate)) + '/hr' : ''}
                          </td>
                          <td></td>
                          <td className="px-4 py-2 text-right">
                            <button onClick={addLaborItem} disabled={!newLaborRoleId} className="bg-primary hover:bg-accent text-white font-semibold px-4 py-1.5 rounded-xl text-xs transition-colors disabled:opacity-40">+ Add</button>
                          </td>
                        </tr>
                        <tr className="border-t-2 border-gray-300 bg-warm">
                          <td colSpan={3} className="px-4 py-3 text-right font-bold text-primary text-sm">Labor Total</td>
                          <td className="px-4 py-3 text-right font-bold text-primary">{fmt(laborTotal)}</td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Non-Labor */}
                <div>
                  <h3 className="text-lg font-bold text-primary mb-3">Non-Labor</h3>
                  <div className="bg-white rounded-2xl shadow overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-warm">
                        <tr>
                          <th className="text-left px-4 py-3 text-primary font-semibold border-b border-gray-200">Description</th>
                          <th className="text-left px-4 py-3 text-primary font-semibold border-b border-gray-200">Category</th>
                          <th className="text-right px-4 py-3 text-primary font-semibold border-b border-gray-200">Units</th>
                          <th className="text-right px-4 py-3 text-primary font-semibold border-b border-gray-200">Per Unit Cost</th>
                          <th className="text-right px-4 py-3 text-primary font-semibold border-b border-gray-200">Total</th>
                          <th className="px-4 py-3 border-b border-gray-200"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {nonLaborItems.map(item => (
                          <tr key={item.id} className="border-t border-gray-100 hover:bg-gray-50">
                            {editingNonLaborId === item.id ? (
                              <>
                                <td className="px-4 py-2"><input autoFocus className="w-full border border-primary rounded px-2 py-1 text-sm focus:outline-none" value={nonLaborDraft.description ?? ''} onChange={e => setNonLaborDraft(d => ({...d, description: e.target.value}))} /></td>
                                <td className="px-4 py-2"><input className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none" value={nonLaborDraft.category ?? ''} onChange={e => setNonLaborDraft(d => ({...d, category: e.target.value}))} /></td>
                                <td className="px-4 py-2"><input type="number" min="0" step="1" className="w-full border border-gray-200 rounded px-2 py-1 text-sm text-right focus:outline-none" value={nonLaborDraft.units ?? ''} onChange={e => setNonLaborDraft(d => ({...d, units: Number(e.target.value)}))} /></td>
                                <td className="px-4 py-2"><input type="number" min="0" step="0.01" className="w-full border border-gray-200 rounded px-2 py-1 text-sm text-right focus:outline-none" value={nonLaborDraft.unit_cost ?? ''} onChange={e => setNonLaborDraft(d => ({...d, unit_cost: Number(e.target.value)}))} /></td>
                                <td className="px-4 py-2 text-right text-gray-400 text-xs">—</td>
                                <td className="px-4 py-2 text-right whitespace-nowrap">
                                  <button onClick={saveNonLaborEdit} className="text-primary hover:text-accent font-semibold text-xs mr-3">Save</button>
                                  <button onClick={() => { setEditingNonLaborId(null); setNonLaborDraft({}); }} className="text-gray-400 hover:text-gray-600 text-xs">Cancel</button>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="px-4 py-3 font-medium text-gray-800">{item.description}</td>
                                <td className="px-4 py-3 text-gray-500">{item.category || <span className="text-gray-300">—</span>}</td>
                                <td className="px-4 py-3 text-right text-gray-600">{Number(item.units).toLocaleString()}</td>
                                <td className="px-4 py-3 text-right text-gray-600">{fmt(Number(item.unit_cost))}</td>
                                <td className="px-4 py-3 text-right font-semibold text-gray-800">{fmt(Number(item.units) * Number(item.unit_cost))}</td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                  <button onClick={() => { setEditingNonLaborId(item.id); setNonLaborDraft({ description: item.description, category: item.category, units: item.units, unit_cost: item.unit_cost }); }} className="text-primary hover:text-accent font-semibold text-xs mr-3">Edit</button>
                                  <button onClick={() => deleteNonLaborItem(item.id)} className="text-red-400 hover:text-red-600 font-semibold text-xs">Delete</button>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                        <tr className="border-t border-gray-200 bg-gray-50">
                          <td className="px-4 py-2"><input className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent" placeholder="Description" value={newNonLabor.description} onChange={e => setNewNonLabor(n => ({...n, description: e.target.value}))} onKeyDown={e => { if (e.key === 'Enter') addNonLaborItem(); }} /></td>
                          <td className="px-4 py-2"><input className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent" placeholder="Category" value={newNonLabor.category} onChange={e => setNewNonLabor(n => ({...n, category: e.target.value}))} onKeyDown={e => { if (e.key === 'Enter') addNonLaborItem(); }} /></td>
                          <td className="px-4 py-2"><input type="number" min="0" step="1" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-accent" placeholder="1" value={newNonLabor.units} onChange={e => setNewNonLabor(n => ({...n, units: e.target.value}))} onKeyDown={e => { if (e.key === 'Enter') addNonLaborItem(); }} /></td>
                          <td className="px-4 py-2"><input type="number" min="0" step="0.01" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-accent" placeholder="0.00" value={newNonLabor.unit_cost} onChange={e => setNewNonLabor(n => ({...n, unit_cost: e.target.value}))} onKeyDown={e => { if (e.key === 'Enter') addNonLaborItem(); }} /></td>
                          <td></td>
                          <td className="px-4 py-2 text-right">
                            <button onClick={addNonLaborItem} disabled={!newNonLabor.description.trim()} className="bg-primary hover:bg-accent text-white font-semibold px-4 py-1.5 rounded-xl text-xs transition-colors disabled:opacity-40">+ Add</button>
                          </td>
                        </tr>
                        <tr className="border-t-2 border-gray-300 bg-warm">
                          <td colSpan={4} className="px-4 py-3 text-right font-bold text-primary text-sm">Non-Labor Total</td>
                          <td className="px-4 py-3 text-right font-bold text-primary">{fmt(nonLaborTotal)}</td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>
            );
          })()}

          {/* Budget / Actuals */}
          {view === 'budget' && currentProject && (() => {
            const fmt = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
            const cols = ['36%','13%','13%','13%','13%','12%'] as const;
            const planLaborBudget = laborItems.reduce((sum, i) => sum + Number(i.hours) * Number(i.hourly_rate || 0), 0);
            const planNLBudget = nonLaborItems.reduce((sum, i) => sum + Number(i.units) * Number(i.unit_cost), 0);
            const planTotal = planLaborBudget + planNLBudget;
            const memberRows = team.map(member => {
              const totalHours = actuals.filter(a => a.team_member_id === member.id).reduce((sum, a) => sum + Number(a.hours), 0);
              const resource = resources.find(r => r.name === member.name);
              const rate = resource?.hourly_rate ? Number(resource.hourly_rate) : 0;
              return { member, totalHours, rate, totalCost: totalHours * rate };
            }).filter(r => r.totalHours > 0);
            const actualLaborHours = memberRows.reduce((sum, r) => sum + r.totalHours, 0);
            const actualLaborCost = memberRows.reduce((sum, r) => sum + r.totalCost, 0);
            const actualNLCost = budgetNonLabor.reduce((sum, i) => sum + Number(i.units) * Number(i.rate), 0);
            const totalActuals = actualLaborCost + actualNLCost;
            const totalVariance = planTotal - totalActuals;
            return (
              <div className="p-6 max-w-4xl mx-auto space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-primary">Budget / Actuals — {currentProject.name}</h2>
                  <div className="flex gap-3 items-end">
                    <div className="bg-primary rounded-2xl px-5 py-3 text-white text-center">
                      <div className="text-xs font-semibold opacity-70 mb-0.5">Budget</div>
                      <div className="text-xl font-extrabold">{fmt(planTotal)}</div>
                    </div>
                    <div className="bg-accent rounded-2xl px-5 py-3 text-white text-center">
                      <div className="text-xs font-semibold opacity-70 mb-0.5">Actuals</div>
                      <div className="text-xl font-extrabold">{fmt(totalActuals)}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <div className="relative">
                        <button onClick={() => setShowBudgetExportMenu(v => !v)} className="bg-white border border-primary text-primary hover:bg-primary hover:text-white font-semibold px-4 py-1.5 rounded-xl transition-colors text-sm">
                          Export ▾
                        </button>
                        {showBudgetExportMenu && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setShowBudgetExportMenu(false)} />
                            <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden">
                              <a href={`${API_URL}/api/projects/${currentProject.id}/budget/export/docx`} onClick={() => setShowBudgetExportMenu(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 font-medium">
                                📝 Word
                              </a>
                              <a href={`${API_URL}/api/projects/${currentProject.id}/budget/export/xlsx`} onClick={() => setShowBudgetExportMenu(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 font-medium border-t border-gray-100">
                                📊 Excel
                              </a>
                            </div>
                          </>
                        )}
                      </div>
                      <div className={`${totalVariance >= 0 ? 'bg-green-600' : 'bg-red-500'} rounded-2xl px-5 py-3 text-white text-center`}>
                        <div className="text-xs font-semibold opacity-70 mb-0.5">Variance</div>
                        <div className="text-xl font-extrabold">{totalVariance >= 0 ? '+' : ''}{fmt(totalVariance)}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-2xl shadow overflow-hidden">
                  <table className="w-full text-sm" style={{tableLayout:'fixed'}}>
                    <colgroup>{cols.map((w,i) => <col key={i} style={{width:w}} />)}</colgroup>
                    <thead className="bg-warm">
                      <tr>
                        <th className="text-left px-4 py-3 text-primary font-semibold">Labor</th>
                        <th className="text-right px-4 py-3 text-primary font-semibold">Budget</th>
                        <th className="text-right px-4 py-3 text-primary font-semibold">Actual Hours</th>
                        <th className="text-right px-4 py-3 text-primary font-semibold">Rate</th>
                        <th className="text-right px-4 py-3 text-primary font-semibold">Actual Cost</th>
                        <th className="text-right px-4 py-3 text-primary font-semibold">Variance</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-gray-100 hover:bg-warm transition-colors cursor-pointer" onClick={() => setLaborExpanded(v => !v)}>
                        <td className="px-4 py-3 font-semibold text-gray-800"><span className="text-xs text-gray-400 select-none mr-2">{laborExpanded ? '▼' : '▶'}</span>Total Labor</td>
                        <td className="px-4 py-3 text-right text-gray-700">{fmt(planLaborBudget)}</td>
                        <td className="px-4 py-3 text-right text-gray-700">{actualLaborHours > 0 ? `${actualLaborHours}h` : <span className="text-gray-300">—</span>}</td>
                        <td className="px-4 py-3 text-right text-gray-300">—</td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-800">{actualLaborCost > 0 ? fmt(actualLaborCost) : <span className="text-gray-300">—</span>}</td>
                        <td className="px-4 py-3 text-right font-semibold">{actualLaborCost > 0 ? <span className={planLaborBudget - actualLaborCost >= 0 ? 'text-green-600' : 'text-red-500'}>{fmt(planLaborBudget - actualLaborCost)}</span> : <span className="text-gray-300">—</span>}</td>
                      </tr>
                      {laborExpanded && memberRows.map(({ member, totalHours, rate, totalCost }) => (
                        <tr key={member.id} className="border-t border-gray-50 bg-gray-50">
                          <td className="px-4 py-2 pl-10 text-gray-600 truncate">{member.name || <span className="text-gray-400 italic">Unassigned</span>}{member.role ? <span className="text-gray-400 text-xs ml-2">({member.role})</span> : null}</td>
                          <td className="px-4 py-2 text-right text-gray-300">—</td>
                          <td className="px-4 py-2 text-right text-gray-600">{totalHours}h</td>
                          <td className="px-4 py-2 text-right text-gray-500">{rate > 0 ? fmt(rate) + '/hr' : <span className="text-gray-300">—</span>}</td>
                          <td className="px-4 py-2 text-right text-gray-700 font-medium">{totalCost > 0 ? fmt(totalCost) : <span className="text-gray-300">—</span>}</td>
                          <td className="px-4 py-2 text-right text-gray-300">—</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bg-white rounded-2xl shadow overflow-hidden">
                  <table className="w-full text-sm" style={{tableLayout:'fixed'}}>
                    <colgroup>{cols.map((w,i) => <col key={i} style={{width:w}} />)}</colgroup>
                    <thead className="bg-warm">
                      <tr>
                        <th className="text-left px-4 py-3 text-primary font-semibold">Non-Labor</th>
                        <th className="text-right px-4 py-3 text-primary font-semibold">Budget</th>
                        <th className="text-right px-4 py-3 text-primary font-semibold">Units</th>
                        <th className="text-right px-4 py-3 text-primary font-semibold">Rate</th>
                        <th className="text-right px-4 py-3 text-primary font-semibold">Actual Cost</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-gray-100 hover:bg-warm transition-colors cursor-pointer" onClick={() => setNonLaborExpanded(v => !v)}>
                        <td className="px-4 py-3 font-semibold text-gray-800"><span className="text-xs text-gray-400 select-none mr-2">{nonLaborExpanded ? '▼' : '▶'}</span>Total Non-Labor</td>
                        <td className="px-4 py-3 text-right text-gray-700">{fmt(planNLBudget)}</td>
                        <td className="px-4 py-3 text-right text-gray-300">—</td>
                        <td className="px-4 py-3 text-right text-gray-300">—</td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-800">{actualNLCost > 0 ? fmt(actualNLCost) : <span className="text-gray-300">—</span>}</td>
                        <td className="px-4 py-3 text-right font-semibold">{actualNLCost > 0 ? <span className={planNLBudget - actualNLCost >= 0 ? 'text-green-600' : 'text-red-500'}>{fmt(planNLBudget - actualNLCost)}</span> : <span className="text-gray-300">—</span>}</td>
                      </tr>
                      {nonLaborExpanded && (<>
                        {budgetNonLabor.map(item => (
                          <tr key={item.id} className="border-t border-gray-50 bg-gray-50 hover:bg-warm transition-colors">
                            {editingBudgetNLId === item.id ? (
                              <>
                                <td className="px-4 py-2"><input autoFocus className="w-full border border-primary rounded px-2 py-1 text-sm focus:outline-none" value={budgetNLDraft.description} onChange={e => setBudgetNLDraft(d => ({...d, description: e.target.value}))} /></td>
                                <td className="px-4 py-2 text-right text-gray-300">—</td>
                                <td className="px-4 py-2"><input type="number" min="0" step="0.01" className="w-full border border-gray-200 rounded px-2 py-1 text-sm text-right focus:outline-none" value={budgetNLDraft.units} onChange={e => setBudgetNLDraft(d => ({...d, units: e.target.value}))} /></td>
                                <td className="px-4 py-2"><input type="number" min="0" step="0.01" className="w-full border border-gray-200 rounded px-2 py-1 text-sm text-right focus:outline-none" value={budgetNLDraft.rate} onChange={e => setBudgetNLDraft(d => ({...d, rate: e.target.value}))} /></td>
                                <td className="px-4 py-2 text-right text-gray-400 text-xs">{fmt((parseFloat(budgetNLDraft.units)||0)*(parseFloat(budgetNLDraft.rate)||0))}</td>
                                <td className="px-4 py-2 text-right whitespace-nowrap">
                                  <button onClick={saveBudgetNLEdit} className="text-primary hover:text-accent font-semibold text-xs mr-2">Save</button>
                                  <button onClick={() => setEditingBudgetNLId(null)} className="text-gray-400 hover:text-gray-600 text-xs">Cancel</button>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="px-4 py-3 text-gray-700 truncate">
                                  <span className="inline-flex items-center gap-1 mr-2">
                                    <button onClick={e => { e.stopPropagation(); setEditingBudgetNLId(item.id); setBudgetNLDraft({ description: item.description, units: String(item.units), rate: String(item.rate) }); }} className="text-gray-300 hover:text-primary align-middle" title="Edit">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>
                                    </button>
                                    <button onClick={e => { e.stopPropagation(); deleteBudgetNL(item.id); }} className="text-gray-300 hover:text-red-500 align-middle" title="Delete">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
                                    </button>
                                  </span>
                                  {item.description}
                                </td>
                                <td className="px-4 py-3 text-right text-gray-300">—</td>
                                <td className="px-4 py-3 text-right text-gray-600">{Number(item.units).toLocaleString()}</td>
                                <td className="px-4 py-3 text-right text-gray-500">{fmt(Number(item.rate))}</td>
                                <td className="px-4 py-3 text-right font-semibold text-gray-800">{fmt(Number(item.units)*Number(item.rate))}</td>
                                <td className="px-4 py-3"></td>
                              </>
                            )}
                          </tr>
                        ))}
                        <tr className="border-t border-gray-200 bg-gray-50">
                          <td className="px-4 py-2"><input className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent" placeholder="Description" value={newBudgetNL.description} onChange={e => setNewBudgetNL(d => ({...d, description: e.target.value}))} /></td>
                          <td className="px-4 py-2 text-right text-gray-300">—</td>
                          <td className="px-4 py-2"><input type="number" min="0" step="0.01" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-accent" placeholder="Units" value={newBudgetNL.units} onChange={e => setNewBudgetNL(d => ({...d, units: e.target.value}))} /></td>
                          <td className="px-4 py-2"><input type="number" min="0" step="0.01" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-accent" placeholder="Rate" value={newBudgetNL.rate} onChange={e => setNewBudgetNL(d => ({...d, rate: e.target.value}))} /></td>
                          <td className="px-4 py-2 text-right text-gray-400 text-xs">{newBudgetNL.units && newBudgetNL.rate ? fmt((parseFloat(newBudgetNL.units)||0)*(parseFloat(newBudgetNL.rate)||0)) : ''}</td>
                          <td className="px-4 py-2 text-right"><button onClick={addBudgetNL} disabled={!newBudgetNL.description.trim()} className="bg-primary hover:bg-accent text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40">+ Add</button></td>
                        </tr>
                      </>)}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

        </SignedIn>
        </div>
      </main>

      {/* Feedback Modal */}
      {feedbackOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-6" onClick={() => setFeedbackOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-8 flex flex-col items-center gap-4" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold text-primary">Share Your Feedback</h2>
            <p className="text-gray-500 text-sm text-center">We'd love to hear from you! Send us your thoughts, ideas, or suggestions.</p>
            <button
              onClick={() => {
                navigator.clipboard.writeText('feedback@gaimplans.com');
                setFeedbackEmailCopied(true);
                setTimeout(() => setFeedbackEmailCopied(false), 2000);
              }}
              className="w-full bg-primary hover:bg-accent text-white font-semibold px-5 py-2.5 rounded-xl transition-colors text-sm"
            >
              {feedbackEmailCopied ? '✓ Copied!' : 'Copy Feedback Email'}
            </button>
            <p className="text-gray-400 text-xs">feedback@gaimplans.com</p>
            <a
              href="mailto:feedback@gaimplans.com?subject=gAImplans%20Feedback"
              className="text-sm text-primary hover:text-accent font-semibold transition-colors"
            >
              Open in Email Client
            </a>
            <button onClick={() => setFeedbackOpen(false)} className="text-sm text-gray-400 hover:text-gray-600 transition-colors mt-1">
              Close
            </button>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6" onClick={() => setShowUpgradeModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-8" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-extrabold" style={{ color: '#3C3489' }}>Upgrade your plan</h2>
                <p className="text-gray-400 text-sm mt-1">Choose the plan that fits your workflow.</p>
              </div>
              <button onClick={() => setShowUpgradeModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Pro card */}
              <div className="rounded-2xl border border-gray-100 p-7 flex flex-col" style={{ backgroundColor: '#FAFAFE' }}>
                <h3 className="text-lg font-extrabold mb-1" style={{ color: '#3C3489' }}>Pro</h3>
                <div className="flex items-end gap-1 mb-1">
                  <span className="text-4xl font-extrabold" style={{ color: '#3C3489' }}>$29</span>
                  <span className="text-sm text-gray-400 pb-1.5">/month</span>
                </div>
                <p className="text-xs text-gray-400 mb-5">Perfect for growing teams</p>
                <ul className="space-y-2.5 mb-8 flex-1">
                  {['Up to 10 projects', 'Unlimited team members', 'AI plan generation', 'AI Coach assistant', 'AI status reports', 'Export to Excel, Word & PDF'].map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="font-bold mt-px shrink-0" style={{ color: '#3C3489' }}>✓</span>{f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => startCheckout(STRIPE_PRO_PRICE_ID)}
                  disabled={checkoutLoading}
                  className="w-full font-bold py-3 rounded-xl text-sm transition-colors text-white disabled:opacity-60"
                  style={{ backgroundColor: '#3C3489' }}
                >
                  {checkoutLoading ? 'Redirecting…' : 'Upgrade to Pro'}
                </button>
              </div>
              {/* Unlimited card */}
              <div className="rounded-2xl p-7 flex flex-col shadow-2xl" style={{ backgroundColor: '#3C3489', color: 'white' }}>
                <div className="text-xs font-bold px-3 py-1 rounded-full inline-block mb-4 self-start" style={{ backgroundColor: '#9FE1CB', color: '#3C3489' }}>Most powerful</div>
                <h3 className="text-lg font-extrabold mb-1">Unlimited</h3>
                <div className="flex items-end gap-1 mb-1">
                  <span className="text-4xl font-extrabold">$59</span>
                  <span className="text-sm opacity-60 pb-1.5">/month</span>
                </div>
                <p className="text-xs opacity-60 mb-5">For power users &amp; agencies</p>
                <ul className="space-y-2.5 mb-8 flex-1">
                  {['Unlimited projects', 'Unlimited team members', 'All Pro features', 'AI Coach assistant', 'AI status reports', 'Priority support'].map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm opacity-85">
                      <span className="font-bold mt-px shrink-0" style={{ color: '#9FE1CB' }}>✓</span>{f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => startCheckout(STRIPE_UNLIMITED_PRICE_ID)}
                  disabled={checkoutLoading}
                  className="w-full font-bold py-3 rounded-xl text-sm transition-colors disabled:opacity-60"
                  style={{ backgroundColor: '#9FE1CB', color: '#3C3489' }}
                >
                  {checkoutLoading ? 'Redirecting…' : 'Upgrade to Unlimited'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Edit Modal */}
      {editingReport && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl flex flex-col" style={{ height: '95vh' }}>
            <div className="flex justify-between items-center px-8 py-5 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-bold text-primary">Status Report</h3>
                <p className="text-xs text-gray-400 mt-0.5">{new Date(editingReport.created_at).toLocaleString()}</p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => saveReport(editingReport)} className="bg-primary hover:bg-accent text-white font-semibold px-5 py-2 rounded-xl transition-colors text-sm">Save</button>
                <button onClick={() => setEditingReport(null)} className="bg-gray-100 hover:bg-gray-200 text-gray-600 font-semibold px-5 py-2 rounded-xl transition-colors text-sm">Cancel</button>
              </div>
            </div>
            <div className="px-8 py-2 border-b border-gray-100 bg-gray-50">
              <p className="text-sm text-gray-600 italic">{editingReport.summary}</p>
            </div>
            <textarea
              className="flex-1 px-8 py-5 text-sm text-gray-700 font-mono resize-none focus:outline-none overflow-y-auto"
              value={editingReport.full_report}
              onChange={e => setEditingReport({ ...editingReport, full_report: e.target.value })}
            />
          </div>
        </div>
      )}

      {/* Players Modal */}
      {showResourcesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-2xl shadow-xl flex flex-col w-full max-w-3xl" style={{maxHeight: '85vh'}}>
            <div className="flex items-center justify-between px-8 py-5 border-b border-gray-100">
              <h3 className="text-xl font-bold text-primary">Players</h3>
              <button onClick={() => { setShowResourcesModal(false); setEditingResourceId(null); setResourceDraft({}); }} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="flex-1 overflow-y-auto px-8 py-6">
              {/* Resource list */}
              {resources.length > 0 && (
                <table className="w-full text-sm mb-6">
                  <thead>
                    <tr className="bg-warm">
                      <th className="text-left px-3 py-2 text-primary font-semibold border-b border-gray-200">Name</th>
                      <th className="text-left px-3 py-2 text-primary font-semibold border-b border-gray-200">Email</th>
                      <th className="text-left px-3 py-2 text-primary font-semibold border-b border-gray-200">Position</th>
                      <th className="text-right px-3 py-2 text-primary font-semibold border-b border-gray-200">Hourly Rate</th>
                      <th className="px-3 py-2 border-b border-gray-200"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {resources.map(r => (
                      <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                        {editingResourceId === r.id ? (
                          <>
                            <td className="px-3 py-2"><input autoFocus className="w-full border border-primary rounded px-2 py-1 text-sm focus:outline-none" value={resourceDraft.name ?? ''} onChange={e => setResourceDraft(d => ({...d, name: e.target.value}))} /></td>
                            <td className="px-3 py-2"><input className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none" value={resourceDraft.email ?? ''} onChange={e => setResourceDraft(d => ({...d, email: e.target.value}))} /></td>
                            <td className="px-3 py-2"><select className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none" value={resourceDraft.role ?? ''} onChange={e => setResourceDraft(d => ({...d, role: e.target.value || null}))}><option value="">— None —</option>{roles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}</select></td>
                            <td className="px-3 py-2"><input type="number" min="0" step="0.01" className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none text-right" value={resourceDraft.hourly_rate ?? ''} onChange={e => setResourceDraft(d => ({...d, hourly_rate: e.target.value ? Number(e.target.value) : null}))} /></td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              <button onClick={saveResourceEdit} className="text-primary hover:text-accent font-semibold text-xs mr-3">Save</button>
                              <button onClick={() => { setEditingResourceId(null); setResourceDraft({}); }} className="text-gray-400 hover:text-gray-600 text-xs">Cancel</button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-3 py-2 font-medium text-gray-800">{r.name}</td>
                            <td className="px-3 py-2 text-gray-500">{r.email || <span className="text-gray-300">—</span>}</td>
                            <td className="px-3 py-2 text-gray-500">{r.role || <span className="text-gray-300">—</span>}</td>
                            <td className="px-3 py-2 text-right text-gray-500">{r.hourly_rate != null ? `$${Number(r.hourly_rate).toFixed(2)}/hr` : <span className="text-gray-300">—</span>}</td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              <button onClick={() => { setEditingResourceId(r.id); setResourceDraft({name: r.name, email: r.email ?? '', role: r.role ?? '', hourly_rate: r.hourly_rate}); }} className="text-primary hover:text-accent font-semibold text-xs mr-3">Edit</button>
                              <button onClick={() => deleteResource(r.id)} className="text-red-400 hover:text-red-600 font-semibold text-xs">Delete</button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {/* Add new resource form */}
              <div className="border border-gray-200 rounded-xl p-5">
                <h4 className="font-semibold text-primary text-sm mb-4">Add Player</h4>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="text-xs text-gray-500 font-semibold mb-1 block">Name *</label>
                    <input autoFocus className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent" placeholder="Full name" value={newResource.name} onChange={e => setNewResource(r => ({...r, name: e.target.value}))} onKeyDown={e => { if (e.key === 'Enter') saveNewResource(); }} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-semibold mb-1 block">Email</label>
                    <input className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent" placeholder="email@example.com" value={newResource.email} onChange={e => setNewResource(r => ({...r, email: e.target.value}))} onKeyDown={e => { if (e.key === 'Enter') saveNewResource(); }} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-semibold mb-1 block">Position</label>
                    <select className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent" value={newResource.role} onChange={e => setNewResource(r => ({...r, role: e.target.value}))}>
                      <option value="">— None —</option>
                      {roles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-semibold mb-1 block">Hourly Rate</label>
                    <input type="number" min="0" step="0.01" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent" placeholder="0.00" value={newResource.hourly_rate} onChange={e => setNewResource(r => ({...r, hourly_rate: e.target.value}))} onKeyDown={e => { if (e.key === 'Enter') saveNewResource(); }} />
                  </div>
                </div>
                <button onClick={saveNewResource} disabled={!newResource.name.trim()} className="bg-primary hover:bg-accent text-white font-semibold px-5 py-2 rounded-xl transition-colors disabled:opacity-40 text-sm">
                  Add Player
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Positions Modal */}
      {showRolesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-2xl shadow-xl flex flex-col w-full max-w-lg" style={{maxHeight: '85vh'}}>
            <div className="flex items-center justify-between px-8 py-5 border-b border-gray-100">
              <h3 className="text-xl font-bold text-primary">Positions</h3>
              <button onClick={() => { setShowRolesModal(false); setEditingRoleId(null); setRoleDraft({}); }} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="flex-1 overflow-y-auto px-8 py-6">
              {roles.length > 0 && (
                <table className="w-full text-sm mb-6">
                  <thead>
                    <tr className="bg-warm">
                      <th className="text-left px-3 py-2 text-primary font-semibold border-b border-gray-200">Position Name</th>
                      <th className="text-right px-3 py-2 text-primary font-semibold border-b border-gray-200">Hourly Rate</th>
                      <th className="px-3 py-2 border-b border-gray-200"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {roles.map(r => (
                      <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                        {editingRoleId === r.id ? (
                          <>
                            <td className="px-3 py-2"><input autoFocus className="w-full border border-primary rounded px-2 py-1 text-sm focus:outline-none" value={roleDraft.name ?? ''} onChange={e => setRoleDraft(d => ({...d, name: e.target.value}))} /></td>
                            <td className="px-3 py-2"><input type="number" min="0" step="0.01" className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none text-right" value={roleDraft.hourly_rate ?? ''} onChange={e => setRoleDraft(d => ({...d, hourly_rate: e.target.value ? Number(e.target.value) : null}))} /></td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              <button onClick={saveRoleEdit} className="text-primary hover:text-accent font-semibold text-xs mr-3">Save</button>
                              <button onClick={() => { setEditingRoleId(null); setRoleDraft({}); }} className="text-gray-400 hover:text-gray-600 text-xs">Cancel</button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-3 py-2 font-medium text-gray-800">{r.name}</td>
                            <td className="px-3 py-2 text-right text-gray-500">{r.hourly_rate != null ? `$${Number(r.hourly_rate).toFixed(2)}/hr` : <span className="text-gray-300">—</span>}</td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              <button onClick={() => { setEditingRoleId(r.id); setRoleDraft({ name: r.name, hourly_rate: r.hourly_rate }); }} className="text-primary hover:text-accent font-semibold text-xs mr-3">Edit</button>
                              <button onClick={() => deleteRole(r.id)} className="text-red-400 hover:text-red-600 font-semibold text-xs">Delete</button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div className="border border-gray-200 rounded-xl p-5">
                <h4 className="font-semibold text-primary text-sm mb-4">Add Position</h4>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="text-xs text-gray-500 font-semibold mb-1 block">Position Name *</label>
                    <input className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent" placeholder="e.g. Developer" value={newRole.name} onChange={e => setNewRole(r => ({...r, name: e.target.value}))} onKeyDown={e => { if (e.key === 'Enter') saveNewRole(); }} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-semibold mb-1 block">Hourly Rate</label>
                    <input type="number" min="0" step="0.01" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent" placeholder="0.00" value={newRole.hourly_rate} onChange={e => setNewRole(r => ({...r, hourly_rate: e.target.value}))} onKeyDown={e => { if (e.key === 'Enter') saveNewRole(); }} />
                  </div>
                </div>
                <button onClick={saveNewRole} disabled={!newRole.name.trim()} className="bg-primary hover:bg-accent text-white font-semibold px-5 py-2 rounded-xl transition-colors disabled:opacity-40 text-sm">
                  Add Position
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-primary">Settings</h3>
              <button onClick={() => setShowSettings(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="space-y-5">
              <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4">Scheduling</h4>
              <label className="flex items-center justify-between gap-4 cursor-pointer">
                <div>
                  <div className="font-semibold text-gray-700">Include weekends in duration</div>
                  <div className="text-sm text-gray-400">When off, Saturday and Sunday are skipped in date calculations</div>
                </div>
                <div
                  onClick={() => saveSettings({ ...settings, include_weekends: !settings.include_weekends })}
                  className={`w-12 h-6 rounded-full transition-colors flex-shrink-0 flex items-center px-1 cursor-pointer ${settings.include_weekends ? 'bg-primary' : 'bg-gray-300'}`}>
                  <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${settings.include_weekends ? 'translate-x-6' : 'translate-x-0'}`} />
                </div>
              </label>
              <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide pt-2">Task Health</h4>
              <label className="flex items-center justify-between gap-4 cursor-pointer">
                <div>
                  <div className="font-semibold text-gray-700">Auto-calculate RAG status</div>
                  <div className="text-sm text-gray-400">Sets task colour based on % complete vs. % of duration elapsed. Won't override manually set statuses.</div>
                </div>
                <div
                  onClick={() => saveSettings({ ...settings, auto_rag: !settings.auto_rag })}
                  className={`w-12 h-6 rounded-full transition-colors flex-shrink-0 flex items-center px-1 cursor-pointer ${settings.auto_rag ? 'bg-primary' : 'bg-gray-300'}`}>
                  <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${settings.auto_rag ? 'translate-x-6' : 'translate-x-0'}`} />
                </div>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {editingTask && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-lg">
            <h3 className="text-xl font-bold text-primary mb-6">Edit Task</h3>
            <div className="grid gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-600 mb-1 block">Task Name</label>
                <input
                  className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  value={editingTask.name}
                  onChange={e => setEditingTask({...editingTask, name: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600 mb-1 block">Description</label>
                <textarea
                  className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent h-20 resize-none"
                  value={editingTask.description}
                  onChange={e => setEditingTask({...editingTask, description: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600 mb-1 block">Assignee</label>
                <select
                  className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  value={editingTask.assignee_id || ''}
                  onChange={e => setEditingTask({...editingTask, assignee_id: e.target.value ? Number(e.target.value) : null})}>
                  <option value="">Unassigned</option>
                  {team.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-600 mb-1 block">Start Date</label>
                  <input type="date" className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                    value={editingTask.start_date || ''}
                    onChange={e => setEditingTask({...editingTask, start_date: e.target.value})} />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600 mb-1 block">End Date</label>
                  <input type="date" className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                    value={editingTask.end_date || ''}
                    onChange={e => setEditingTask({...editingTask, end_date: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600 mb-1 block">Priority</label>
                <select className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  value={editingTask.priority}
                  onChange={e => setEditingTask({...editingTask, priority: e.target.value})}>
                  <option>High</option>
                  <option>Medium</option>
                  <option>Low</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600 mb-1 block">Status</label>
                <select className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  value={editingTask.status}
                  onChange={e => setEditingTask({...editingTask, status: e.target.value})}>
                  <option>Not Started</option>
                  <option>In Progress</option>
                  <option>Blocked</option>
                  <option>Complete</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => updateTask(editingTask)} className="bg-primary hover:bg-accent text-white font-semibold px-6 py-2 rounded-xl transition-colors">Save Changes</button>
              <button onClick={() => setEditingTask(null)} className="bg-gray-100 hover:bg-gray-200 text-gray-600 font-semibold px-6 py-2 rounded-xl transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}

    {/* Dependencies Modal */}
      {depsModalTaskId !== null && (() => {
        const task = tasks.find(t => t.id === depsModalTaskId);
        if (!task) return null;
        return (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
              <h3 className="font-bold text-gray-800 mb-1">Edit Predecessors</h3>
              <p className="text-sm text-gray-500 mb-4">{task.name}</p>
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-xl p-3 mb-6">
                {phases.map(phase => {
                  const phaseTasks = tasks.filter(t => t.phase_name === phase && t.id !== depsModalTaskId);
                  if (phaseTasks.length === 0) return null;
                  return (
                    <div key={phase} className="mb-3">
                      <div className="text-xs font-bold text-primary uppercase mb-1">{phase}</div>
                      {phaseTasks.map(t => (
                        <label key={t.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50 rounded px-1">
                          <input
                            type="checkbox"
                            checked={depsModalSelected.includes(t.id)}
                            onChange={e => setDepsModalSelected(prev =>
                              e.target.checked ? [...prev, t.id] : prev.filter(id => id !== t.id)
                            )}
                          />
                          <span className="text-xs text-gray-400 w-6">#{taskNumbers.get(t.id)}</span>
                          <span className="text-sm text-gray-700">{t.name}</span>
                        </label>
                      ))}
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDepsModalTaskId(null)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-600 font-semibold px-6 py-2 rounded-xl transition-colors">
                  Cancel
                </button>
                <button
                  onClick={() => { updateDependencies(depsModalTaskId, depsModalSelected); setDepsModalTaskId(null); }}
                  className="bg-primary hover:bg-accent text-white font-semibold px-6 py-2 rounded-xl transition-colors">
                  Save
                </button>
              </div>
            </div>
          </div>
        );
      })()}

    {/* Prompt Modal */}
      {promptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm">
            <p className="text-gray-700 font-semibold mb-4">{promptModal.label}</p>
            <input
              autoFocus
              type="text"
              value={promptModal.value}
              onChange={e => setPromptModal({ ...promptModal, value: e.target.value })}
              onKeyDown={e => {
                if (e.key === 'Enter' && promptModal.value.trim()) {
                  promptModal.onSubmit(promptModal.value.trim());
                  setPromptModal(null);
                } else if (e.key === 'Escape') {
                  setPromptModal(null);
                }
              }}
              className="w-full border border-gray-300 rounded-xl px-4 py-2 text-sm mb-6 focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setPromptModal(null)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-600 font-semibold px-6 py-2 rounded-xl transition-colors">
                Cancel
              </button>
              <button
                onClick={() => { if (promptModal.value.trim()) { promptModal.onSubmit(promptModal.value.trim()); setPromptModal(null); } }}
                disabled={!promptModal.value.trim()}
                className="bg-primary hover:bg-accent text-white font-semibold px-6 py-2 rounded-xl transition-colors disabled:opacity-40">
                Add Phase
              </button>
            </div>
          </div>
        </div>
      )}

    {/* AI Coach chat panel (project-specific, opened from schedule tab button) */}
      {currentProject && chatOpen && (
        <div className="fixed bottom-20 right-6 w-96 bg-white rounded-2xl shadow-2xl flex flex-col z-50 border border-gray-100" style={{ height: '520px' }}>
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 rounded-t-2xl bg-primary">
            <div>
              <p className="text-white font-bold text-sm">AI Coach</p>
              <p className="text-purple-200 text-xs">{currentProject.name}</p>
            </div>
            <button onClick={() => setChatOpen(false)} className="text-white hover:text-purple-200 text-xl leading-none">&times;</button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {chatMessages.length === 0 && (
              <p className="text-gray-400 text-sm text-center mt-8">Ask me anything about this project — tasks, risks, team workload, progress, or next steps.</p>
            )}
            {chatMessages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`rounded-2xl px-4 py-2 text-sm max-w-[80%] whitespace-pre-wrap ${msg.role === 'user' ? 'bg-primary text-white rounded-br-sm' : 'bg-gray-100 text-gray-800 rounded-bl-sm'}`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-2 text-sm text-gray-400">Thinking…</div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          <div className="px-4 py-3 border-t border-gray-100 flex gap-2">
            <input
              type="text"
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
              placeholder="Ask about this project…"
              className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <button onClick={sendChat} disabled={chatLoading || !chatInput.trim()} className="bg-primary hover:bg-accent text-white font-semibold px-4 py-2 rounded-xl transition-colors disabled:opacity-40 text-sm">
              Send
            </button>
          </div>
        </div>
      )}

      {/* Help chat (always available) */}
      {helpOpen && (
        <div className="fixed bottom-20 right-6 w-96 bg-white rounded-2xl shadow-2xl flex flex-col z-50 border border-gray-100" style={{ height: '520px' }}>
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 rounded-t-2xl" style={{backgroundColor: '#534AB7'}}>
            <div>
              <p className="text-white font-bold text-sm">Help</p>
              <p className="text-purple-200 text-xs">How do I…</p>
            </div>
            <button onClick={() => setHelpOpen(false)} className="text-white hover:text-purple-200 text-xl leading-none">&times;</button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {helpMessages.length === 0 && (
              <p className="text-gray-400 text-sm text-center mt-8">Ask me how to use any feature — "How do I add a dependency?", "How does RAG status work?", etc.</p>
            )}
            {helpMessages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`rounded-2xl px-4 py-2 text-sm max-w-[80%] whitespace-pre-wrap ${msg.role === 'user' ? 'text-white rounded-br-sm' : 'bg-gray-100 text-gray-800 rounded-bl-sm'}`} style={msg.role === 'user' ? {backgroundColor: '#534AB7'} : {}}>
                  {msg.content}
                </div>
              </div>
            ))}
            {helpLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-2 text-sm text-gray-400">Thinking…</div>
              </div>
            )}
            <div ref={helpEndRef} />
          </div>
          <div className="px-4 py-2 border-t border-gray-100 flex items-center justify-center gap-2">
            <a
              href="mailto:support@gaimplans.com?subject=gAImplans%20Support%20Request"
              className="text-xs text-purple-400 hover:text-purple-600 transition-colors"
            >
              Contact Support
            </a>
            <button
              onClick={() => {
                navigator.clipboard.writeText('support@gaimplans.com');
                setSupportEmailCopied(true);
                setTimeout(() => setSupportEmailCopied(false), 2000);
              }}
              title="Copy email address"
              className="text-xs text-gray-300 hover:text-purple-400 transition-colors"
            >
              {supportEmailCopied ? <span className="text-green-500">Copied!</span> : '📋'}
            </button>
          </div>
          <div className="px-4 py-3 border-t border-gray-100 flex gap-2">
            <input
              type="text"
              value={helpInput}
              onChange={e => setHelpInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendHelp(); } }}
              placeholder="How do I…"
              className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <button onClick={sendHelp} disabled={helpLoading || !helpInput.trim()} className="text-white font-semibold px-4 py-2 rounded-xl transition-colors disabled:opacity-40 text-sm" style={{backgroundColor: '#534AB7'}}>
              Send
            </button>
          </div>
        </div>
      )}
      <button
        onClick={() => { setHelpOpen(v => !v); }}
        className="fixed bottom-6 right-6 bg-accent hover:bg-primary text-white rounded-full shadow-lg transition-colors z-50 flex items-center justify-center font-bold text-lg"
        title="Help"
        style={{ width: '52px', height: '52px' }}>
        {helpOpen ? '✕' : '?'}
      </button>

    {/* Cascade Date Modal */}
      {cascadeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
            <h3 className="text-lg font-bold text-primary mb-2">Update Dependent Tasks?</h3>
            <p className="text-sm text-gray-600 mb-4">The following dependent tasks would be shifted to match the new date:</p>
            <ul className="mb-6 space-y-1 max-h-48 overflow-y-auto">
              {cascadeModal.cascadeChanges.map(c => (
                <li key={c.taskId} className="text-sm text-gray-700 flex justify-between gap-4">
                  <span className="font-medium truncate">{c.name}</span>
                  <span className="text-gray-400 shrink-0">{c.newStart} → {c.newEnd}</span>
                </li>
              ))}
            </ul>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setCascadeModal(null)} className="bg-gray-100 hover:bg-gray-200 text-gray-600 font-semibold px-4 py-2 rounded-xl transition-colors text-sm">Cancel</button>
              <button onClick={async () => { await cascadeModal.onSaveOnly(); setCascadeModal(null); }} className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-4 py-2 rounded-xl transition-colors text-sm">This task only</button>
              <button onClick={async () => { await cascadeModal.onSaveWithCascade(); setCascadeModal(null); }} className="bg-primary hover:bg-accent text-white font-semibold px-4 py-2 rounded-xl transition-colors text-sm">Update all</button>
            </div>
          </div>
        </div>
      )}

    {/* Confirm Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm text-center">
            <p className="text-gray-700 font-medium mb-6">{confirmMessage}</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => { confirmAction(); setConfirmAction(null); setConfirmMessage(''); }}
                className="bg-red-500 hover:bg-red-600 text-white font-semibold px-6 py-2 rounded-xl transition-colors">
                Delete
              </button>
              <button
                onClick={() => { setConfirmAction(null); setConfirmMessage(''); }}
                className="bg-gray-100 hover:bg-gray-200 text-gray-600 font-semibold px-6 py-2 rounded-xl transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default App;